import { createContext, useState, useEffect, useMemo, useContext } from 'react';
import { RoomsType, FormEstateType, UsageType, ProviderType, FloorsType, PlaceType, FormEstateSubType, FormDistributionType, FormFeatureType, FormProjectType, FormEnergyType, UseFor, DistributionType, ListingsDisplayType, FormLocationType } from '@aviv-search/types';
import { jsx } from 'react/jsx-runtime';
import { mapFormModelToSearchServiceModel } from '@aviv-search/transformers';
import { useFormContext, useWatch } from 'react-hook-form';
import { useUpdateEffect, useDebounce } from 'react-use';
import { chain, map } from 'lodash';
import axios from 'axios';
import i18next from 'i18next';
import intervalPlural from 'i18next-intervalplural-postprocessor';

// src/helpers/context/SearchContext/SearchContext.tsx
var StandardBedroomOptions = [
  RoomsType.ONE,
  RoomsType.TWO,
  RoomsType.THREE,
  RoomsType.FOUR,
  RoomsType.FIVE,
  RoomsType.SIX,
  RoomsType.SEVEN,
  RoomsType.EIGHT,
  RoomsType.NINE,
  RoomsType.TEN
];
var ExtendedRoomOptions = [
  RoomsType.ONE,
  RoomsType.ONE_AND_HALF,
  RoomsType.TWO,
  RoomsType.TWO_AND_HALF,
  RoomsType.THREE,
  RoomsType.THREE_AND_HALF,
  RoomsType.FOUR,
  RoomsType.FOUR_AND_HALF,
  RoomsType.FIVE,
  RoomsType.SIX,
  RoomsType.SEVEN,
  RoomsType.EIGHT,
  RoomsType.NINE,
  RoomsType.TEN,
  RoomsType.ELEVEN,
  RoomsType.TWELVE,
  RoomsType.THIRTEEN,
  RoomsType.FOURTEEN,
  RoomsType.FIFTEEN
];
var StandardRoomOptions = [
  RoomsType.ONE,
  RoomsType.TWO,
  RoomsType.THREE,
  RoomsType.FOUR,
  RoomsType.FIVE,
  RoomsType.SIX,
  RoomsType.SEVEN,
  RoomsType.EIGHT,
  RoomsType.NINE,
  RoomsType.TEN,
  RoomsType.ELEVEN,
  RoomsType.TWELVE,
  RoomsType.THIRTEEN,
  RoomsType.FOURTEEN,
  RoomsType.FIFTEEN
];

// src/helpers/templates/filters/ruleSet.template.ts
var ruleSet = (defaultFilterConditions, estateSubTypeOptions, rules) => (selectedUsageType, selectedDistributionType, selectedEstateType) => {
  const distributionTypeMap = rules[selectedUsageType];
  const estateTypeMap = distributionTypeMap ? distributionTypeMap[selectedDistributionType] : void 0;
  const whiteListRules = estateTypeMap ? estateTypeMap[selectedEstateType] : {};
  return {
    ...defaultFilterConditions,
    ...whiteListRules,
    // Collect all allowed types depending on the corresponding brand configuration
    usageTypes: Object.keys(rules || {}),
    distributionTypes: Object.keys(distributionTypeMap || {}),
    estateTypes: Object.keys(estateTypeMap || {}),
    // Prioritize estateSubTypes from whiteListRules if they exist
    estateSubTypes: whiteListRules?.estateSubTypes || estateSubTypeOptions[selectedEstateType] || []
  };
};

// src/helpers/templates/defaultTemplates.ts
var defaultTemplates = {
  filters: {
    rooms: StandardRoomOptions,
    bedrooms: StandardBedroomOptions,
    estateTypesMinimal: [],
    energyCertificateClasses: [
      {
        value: "A_PLUS",
        label: "A+",
        barLabel: "< 50",
        size: 0,
        color: "color.scales.energy.blue100"
      }
    ],
    energyTypes: [],
    locationTypes: [],
    ruleSet: ruleSet(
      {
        showRoomsFilter: false,
        showBedroomsFilter: true,
        showSpaceFilter: false,
        showPlotFilter: false,
        showYearofConstructionFilter: false,
        showSubsidizingHousing: true,
        showFloorsFilter: false,
        showProviderTypeFilter: false,
        showEnergyCertificate: true,
        showListingsDisplay: false,
        showUsageTypeTabs: true,
        showEnergyTypeFilter: false,
        showDistributionTypeButtonGroupFilter: false,
        showCommuteFilter: false,
        usageTypes: [UsageType.RESIDENTIAL, UsageType.COMMERCIAL],
        estateTypes: [],
        distributionTypes: [],
        features: [],
        estateSubTypes: [],
        providerType: [ProviderType.PRIVATE, ProviderType.PROFESSIONAL],
        floors: [FloorsType.ROOF_STOREY, FloorsType.GROUND_FLOOR, FloorsType.OTHER],
        projectTypes: [],
        listingsDisplay: [],
        energyTypes: []
      },
      {
        [FormEstateType.AGRICULTURE_FORESTRY]: [],
        [FormEstateType.APARTMENT]: [],
        [FormEstateType.GASTRONOMY_HOTEL]: [],
        [FormEstateType.HOUSE]: [],
        [FormEstateType.HOUSE_APARTMENT]: [],
        [FormEstateType.MISCELLANEOUS]: [],
        [FormEstateType.OFFICE]: [],
        [FormEstateType.PARKING]: [],
        [FormEstateType.PLOT]: [],
        [FormEstateType.STORAGE_PRODUCTION]: [],
        [FormEstateType.TRADING]: []
      },
      {}
    )
  }
};
var defaultContext = {
  locale: "en",
  brand: "immonet",
  country: "de",
  stage: "dev",
  variant: "nonFlagship",
  defaultLocation: { id: "", coordinates: { lat: 0, lng: 0 } },
  templates: defaultTemplates,
  countMode: "default",
  searchType: "classified",
  transformers: {
    useFor: {}
  }
};
var MFEContext = createContext(defaultContext);
var useMFEContext = () => useContext(MFEContext);
var useBrand = () => useMFEContext().brand;
var useLocale = () => useMFEContext().locale;
var useCountry = () => useMFEContext().country;
var useStage = () => useMFEContext().stage;
var useTemplates = () => useMFEContext().templates;
var useVariant = () => useMFEContext().variant;
var useCountMode = () => useMFEContext().countMode;
var useSearchType = () => useMFEContext().searchType;
var useDefaultLocation = () => useMFEContext().defaultLocation;
var useTransformers = () => useMFEContext().transformers;
var MFEProvider = ({
  children,
  brand,
  locale,
  country,
  stage,
  templates,
  variant,
  countMode,
  searchType,
  defaultLocation,
  transformers
}) => {
  const {
    brand: contextBrand,
    locale: contextLocale,
    country: contextCountry,
    stage: contextStage,
    templates: contextTemplates,
    variant: contextVariant,
    countMode: contextCountMode,
    searchType: contextSearchType,
    defaultLocation: contextDefaultLocation,
    transformers: contextTransformers
  } = useMFEContext();
  const updatedContext = {
    brand: brand || contextBrand,
    locale: locale || contextLocale,
    country: country || contextCountry,
    stage: stage || contextStage,
    templates: templates || contextTemplates,
    variant: variant || contextVariant,
    countMode: countMode || contextCountMode,
    searchType: searchType || contextSearchType,
    defaultLocation: defaultLocation || contextDefaultLocation,
    transformers: transformers || contextTransformers
  };
  return /* @__PURE__ */ jsx(MFEContext.Provider, { value: updatedContext, children });
};
var useConditionalFilter = () => {
  const { watch } = useFormContext();
  const { usageType, distributionType, estateType } = watch();
  return useTemplates().filters.ruleSet(usageType, distributionType, estateType);
};
var roomsTypeDefaultValue = { min: RoomsType.ANY, max: RoomsType.ANY };
var minMaxTypeDefaultValue = {
  min: void 0,
  max: void 0
};
var subsidizingTypeDefaultValue = void 0;
var providerTypeDefaultValue = [];
var energyCertificateTypeDefaultValue = [];
var floorsTypeDefaultValue = [
  FloorsType.ROOF_STOREY,
  FloorsType.GROUND_FLOOR,
  FloorsType.OTHER
];

// src/helpers/hooks/conditionalFilters/useConditionalFilterReset.ts
var useConditionalFilterReset = () => {
  const { resetField, getValues } = useFormContext();
  const {
    showRoomsFilter,
    showSpaceFilter,
    showPlotFilter,
    showYearofConstructionFilter,
    showSubsidizingHousing,
    showFloorsFilter,
    showProviderTypeFilter,
    showEnergyCertificate,
    showBedroomsFilter,
    showEnergyTypeFilter
  } = useConditionalFilter();
  const spaceValue = getValues("space");
  const plotSpaceValue = getValues("plotSpace");
  const yearOfConstructionValue = getValues("yearOfConstruction");
  const roomsValue = getValues("rooms");
  const bedroomsValue = getValues("bedrooms");
  useEffect(() => {
    if (!showRoomsFilter && (!roomsValue || roomsValue.min == null && roomsValue.max == null)) {
      resetField("rooms", { defaultValue: { ...roomsTypeDefaultValue } });
    }
    if (!showBedroomsFilter && (!bedroomsValue || bedroomsValue.min == null && bedroomsValue.max == null)) {
      resetField("bedrooms", { defaultValue: { ...roomsTypeDefaultValue } });
    }
    if (!showSpaceFilter && (!spaceValue || spaceValue.min == null && spaceValue.max == null)) {
      resetField("space", { defaultValue: { ...minMaxTypeDefaultValue } });
    }
    if (!showPlotFilter && (!plotSpaceValue || plotSpaceValue.min == null && plotSpaceValue.max == null)) {
      resetField("plotSpace", { defaultValue: { ...minMaxTypeDefaultValue } });
    }
    if (!showYearofConstructionFilter && (!yearOfConstructionValue || yearOfConstructionValue.min == null && yearOfConstructionValue.max == null)) {
      resetField("yearOfConstruction", { defaultValue: { ...minMaxTypeDefaultValue } });
    }
    if (!showSubsidizingHousing)
      resetField("subsidizingHousing", { defaultValue: subsidizingTypeDefaultValue });
    if (!showFloorsFilter)
      resetField("floors", { defaultValue: floorsTypeDefaultValue });
    if (!showProviderTypeFilter)
      resetField("providerType", { defaultValue: providerTypeDefaultValue });
    if (!showEnergyCertificate)
      resetField("energyCertificate", { defaultValue: energyCertificateTypeDefaultValue });
    if (!showEnergyTypeFilter)
      resetField("energyTypes", { defaultValue: [] });
  }, [
    showRoomsFilter,
    showSpaceFilter,
    showPlotFilter,
    showYearofConstructionFilter,
    showSubsidizingHousing,
    showFloorsFilter,
    showProviderTypeFilter,
    showEnergyCertificate,
    showEnergyTypeFilter
  ]);
};
var useConditionalFilterUpdateEstateSubTypes = () => {
  const { getValues, setValue } = useFormContext();
  const { estateSubTypes } = useConditionalFilter();
  useEffect(() => {
    const updated = getValues("estateSubTypes")?.filter((estateSubType) => estateSubTypes.includes(estateSubType));
    setValue("estateSubTypes", updated);
  }, [estateSubTypes]);
};
var useConditionalFilterUpdateEstateTypes = () => {
  const { setValue, getValues } = useFormContext();
  const { estateTypes } = useConditionalFilter();
  useEffect(() => {
    if (estateTypes.length > 0 && !estateTypes.includes(getValues("estateType")))
      setValue("estateType", estateTypes[0]);
  }, [estateTypes]);
};
var useConditionalFilterUpdateFeatures = () => {
  const { getValues, setValue } = useFormContext();
  const { features } = useConditionalFilter();
  useEffect(() => {
    const nextFeatures = getValues("features")?.filter((feature) => features.includes(feature));
    setValue("features", nextFeatures);
  }, [features]);
};
var useConditionalFilterUpdateProjectTypes = () => {
  const { setValue } = useFormContext();
  const { projectTypes } = useConditionalFilter();
  useUpdateEffect(() => {
    setValue("projectTypes", projectTypes);
  }, [projectTypes]);
};

// src/helpers/hooks/conditionalFilters/useConditionalFilters.ts
var useConditionalFilters = () => {
  useConditionalFilterReset();
  useConditionalFilterUpdateEstateSubTypes();
  useConditionalFilterUpdateEstateTypes();
  useConditionalFilterUpdateFeatures();
  useConditionalFilterUpdateProjectTypes();
};

// src/helpers/utils/getServiceUrl.ts
var getServiceUrl = (stage, brand) => stage === "live" || stage === "preview" ? "/search-mfe-bff" : `https://${brand}.mfebff.funny-frog-${stage}.aws.aviv.eu/search-mfe-bff`;
var getInternalServiceUrl = (stage, brand) => `https://${brand}-mfebff.funny-frog-${stage}.aws.aviv-internal.eu/search-mfe-bff`;

// src/helpers/hooks/useServiceUrl.ts
var useServiceUrl = () => {
  const stage = useStage();
  const brand = useBrand();
  return getServiceUrl(stage, brand);
};

// src/helpers/utils/isModelValid.ts
var isLocationValid = (location) => {
  if (!location)
    return false;
  return (location.placeIds?.length ?? 0) > 0 || (location.polylines?.length ?? 0) > 0;
};
var isModelValid = ({
  location,
  priceMax,
  priceMin,
  spaceMax,
  spaceMin,
  yearOfConstructionMin,
  yearOfConstructionMax,
  numberOfRoomsMax,
  numberOfRoomsMin,
  plotSpaceMin,
  plotSpaceMax
}) => {
  if (!isLocationValid(location))
    return false;
  if (priceMax && priceMin && priceMax < priceMin)
    return false;
  if (numberOfRoomsMax && numberOfRoomsMin && numberOfRoomsMax < numberOfRoomsMin)
    return false;
  if (yearOfConstructionMax && yearOfConstructionMin && yearOfConstructionMax < yearOfConstructionMin)
    return false;
  if (spaceMax && spaceMin && spaceMax < spaceMin)
    return false;
  if (plotSpaceMax && plotSpaceMin && plotSpaceMax < plotSpaceMin)
    return false;
  return true;
};
var parentTypes = [
  PlaceType.NEIGHBORHOOD,
  PlaceType.MUNICIPALITY,
  PlaceType.PROVINCE,
  PlaceType.REGION,
  PlaceType.STREETS
];
var fetchPlaces = async (placesIds, serviceUrl) => {
  const config = {
    params: { placesIds, parentTypes }
  };
  const url = `${serviceUrl}/places/data`;
  const result = await axios.get(url, config).catch(() => ({ data: { places: [] } }));
  return result?.data?.places ?? [];
};

// src/helpers/utils/getLocationsData.ts
var collectPlaceIds = (locations) => chain(locations).filter(({ type }) => type === FormLocationType.CITY).map(({ districts = [], boroughs = [] }) => chain(districts).concat(boroughs).map("id").value()).flatten().concat(map(locations, "id")).uniq().value();
var enrichDistrictsOrBoroughs = (districtsOrBoroughs, places) => districtsOrBoroughs.map((districtOrBorough) => {
  const place = places.find(({ placeId }) => placeId === districtOrBorough.id);
  return {
    ...districtOrBorough,
    labels: place?.labels ?? [],
    postalCodes: place?.postal_codes ?? []
  };
});
var enrichLocations = (locations, places) => locations.map((location) => {
  const { id, districts = [], boroughs = [], type, ...rest } = location;
  const place = places.find(({ placeId }) => placeId === id);
  if (!place)
    return location;
  const updatedType = type === FormLocationType.ID && place.type_key === PlaceType.MUNICIPALITY ? FormLocationType.CITY : type;
  const coords = place?.coordinates;
  return {
    ...rest,
    id,
    type: updatedType,
    labels: place?.labels ?? [],
    postalCodes: place?.postal_codes ?? [],
    ...coords ? {
      coordinates: coords.max_inscribed_circle ?? {
        lat: coords.lat,
        lng: coords.lng
      }
    } : {},
    parentIds: place?.parents?.map(({ placeId }) => placeId) ?? [],
    districts: enrichDistrictsOrBoroughs(districts, places),
    boroughs: enrichDistrictsOrBoroughs(boroughs, places)
  };
});
var transformDistrictsAndBoroughs = (locations, places) => {
  const getPlaceById = (id) => places.find(({ placeId }) => placeId === id);
  return locations.reduce((list, location) => {
    if (location.type === FormLocationType.RADIUS) {
      list.push(location);
      return list;
    }
    const place = getPlaceById(location.id);
    if (!place)
      return list;
    if (place.type_key === PlaceType.NEIGHBORHOOD || place.type_key === PlaceType.BOROUGHS) {
      const variant = place.type_key === PlaceType.NEIGHBORHOOD ? "districts" : "boroughs";
      const city = place.parents.find((parent) => parent.type_key === PlaceType.MUNICIPALITY);
      if (!city)
        return list;
      const districtOrBorough = {
        id: place.placeId,
        labels: place.labels,
        postalCodes: place.postal_codes
      };
      const existingCityIndex = list.findIndex(({ id }) => id === city.placeId);
      const existingCity = list.at(existingCityIndex);
      if (existingCity) {
        if (existingCity[variant]?.findIndex(({ id }) => id === place.placeId) === -1) {
          existingCity[variant]?.push(districtOrBorough);
          list.splice(existingCityIndex, 1, existingCity);
        }
        return list;
      }
      list.push({
        id: city.placeId,
        type: FormLocationType.CITY,
        labels: city.labels ?? [],
        postalCodes: city.postal_codes ?? [],
        parentIds: place.parents.map(({ placeId }) => placeId).filter((placeId) => placeId !== city.placeId),
        [variant]: [districtOrBorough],
        [variant === "districts" ? "boroughs" : "districts"]: []
      });
      return list;
    }
    list.push(location);
    return list;
  }, []);
};
var getLocationsData = async ({
  locations = [],
  serviceUrl
}) => {
  if (locations.length === 0)
    return [];
  const placeIds = collectPlaceIds(locations);
  const places = await fetchPlaces(placeIds, serviceUrl);
  const transformed = transformDistrictsAndBoroughs(locations, places);
  return enrichLocations(transformed, places);
};

// src/helpers/hooks/useUpdateLocations.ts
var useUpdateLocations = () => {
  const serviceUrl = useServiceUrl();
  const { getValues, setValue } = useFormContext();
  const [isUpdated, setIsUpdated] = useState(false);
  useEffect(() => {
    const { locations } = getValues();
    getLocationsData({ locations, serviceUrl }).then((updated) => {
      setValue("locations", updated);
    }).finally(() => {
      setIsUpdated(true);
    });
  }, []);
  return isUpdated;
};

// src/i18n/locales/de/refiner.json
var refiner_default = {
  filters: {
    area: {
      title: "Fl\xE4che"
    },
    bedrooms_range: {
      title: "Schlafzimmer"
    },
    common: {
      button_group: {
        all: "Alle zeigen",
        exclude: "Ausschlie\xDFen",
        include: "Nur relevant"
      },
      range: {
        input_field: {
          error_message: {
            max: "Der Maximalwert muss \xFCber dem Mindestwert liegen.",
            min: "Der Mindestwert muss kleiner als der Maximalwert sein."
          }
        },
        label: {
          max: "Max",
          min: "Min"
        }
      }
    },
    construction_year: {
      title: "Baujahr"
    },
    context_switch: {
      commercial: "Gewerbe",
      residential: "Wohnen"
    },
    distribution_type: {
      button_group: {
        buy: "Kaufen",
        rent: "Mieten"
      }
    },
    draw_on_map: {
      button: {
        label: "Suchbereich einzeichnen"
      }
    },
    energy_performance: {
      subtitle: "Prim\xE4renergieverbrauch (in kWh pro m\xB2 und Jahr)",
      title: "Energieeffizienzklasse"
    },
    energy_type: {
      checkbox: {
        collective: "Zentralheizung",
        heatpump: "W\xE4rmepumpe",
        solar_electricity: "Sonnenkollektoren"
      },
      title: "Energie und Heizung"
    },
    estate_subtype: {
      agriculture_forestry: {
        checkbox: {
          agriculture_company: "Landwirtschaftlicher Betrieb",
          farm_ranch: "Bauernhof",
          forestry: "Forstwirtschaft",
          gardening_orchard: "Gartenarbeit/Obstgarten",
          hunting_fishing: "Jagd und Fischerei",
          riding: "Reiterhof",
          winery: "Weingut"
        },
        title: "Objektart"
      },
      apartment: {
        checkbox: {
          loft: "Loft",
          multi_storey: "Maisonette",
          penthouse: "Penthouse",
          studio: "Studio",
          terrace: "Terrassenwohnung"
        },
        title: "Wohnungstyp"
      },
      gastronomy_hotel: {
        checkbox: {
          cafe_bar_pub: "Caf\xE9/Bar/Kneipe",
          club_discotheque: "Club/Diskothek",
          hotel: "Hotel",
          hotel_pension: "Hotel",
          restaurant: "Restaurant"
        },
        title: "Objektart"
      },
      house: {
        checkbox: {
          apartment_house: "Mehrparteienhaus",
          bungalow: "Bungalow",
          castle: "Schloss",
          chalet: "Chalet",
          farmhouse: "Bauernhaus",
          finca: "Finca",
          manor_house: "Herrenhaus",
          mansion: "Herrenhaus",
          multi_family_house: "Mehrfamilienhaus",
          rustico: "Rustico",
          semidetached_house: "Doppelhaush\xE4lfte",
          single_family_house: "Einfamilienhaus",
          terrace_house: "Reihenhaus",
          town_house: "Stadthaus",
          villa: "Villa"
        },
        title: "Haustyp"
      },
      office: {
        checkbox: {
          atelier: "Atelier",
          coworking: "Coworking",
          living_and_commercial_building: "Wohn- und Gesch\xE4ftshaus",
          medical: "Praxis",
          office: "B\xFCro"
        },
        title: "Objektart"
      },
      parking: {
        checkbox: {
          carport: "Carport",
          duplex: "Doppelgarage",
          garage: "Garage",
          outside: "Au\xDFenstellplatz",
          underground_garage: "Tiefgarage",
          underground_parking_space: "Tiefgaragenstellplatz"
        },
        title: "Stellplatztyp"
      },
      plot: {
        checkbox: {
          commercial: "Gewerbliche Nutzung",
          leisure_facility: "Freizeit",
          living: "Wohnen"
        },
        title: "Grundst\xFCcksart"
      },
      storage_production: {
        checkbox: {
          garage_repair: "Werkstatt",
          logistics_center: "Logistik-Center",
          misc_storage: "Sonstige Lagerung",
          outdoor_space: "Au\xDFenfl\xE4che",
          production_hall: "Industriehalle",
          warehouse_hall: "Lagerhalle"
        },
        title: "Objektart"
      },
      trading: {
        checkbox: {
          boutique: "Boutique",
          kiosk: "Kiosk",
          sales_area: "Verkaufsfl\xE4che",
          shopping_centre: "Einkaufszentrum",
          showroom_space: "Ausstellungsfl\xE4che",
          store: "Laden"
        },
        title: "Objektart"
      }
    },
    estate_type: {
      checkbox: {
        agriculture_forestry: "Land-/Forstwirtschaft",
        apartment: "Wohnung",
        commercial: "Gewerbeimmobilien",
        gastronomy_hotel: "Gastronomie/Hotel",
        house: "Haus",
        house_apartment: "Haus oder Wohnung",
        investment: "Anlageobjekte",
        miscellaneous: "Sonstiges",
        new_builds: "Neubauprojekte",
        office: "B\xFCro-/Praxisfl\xE4che",
        parking: "Parkplatz",
        plot: "Grundst\xFCck",
        shared_flats: "Wohngemeinschaften",
        storage_production: "Halle/Industriefl\xE4che",
        temporary_living: "Wohnen auf Zeit",
        trading: "Ladenfl\xE4che"
      }
    },
    features: {
      checkbox: {
        balcony_terrace: "Balkon/Terrasse",
        bathroom_with_bathtub: "Badezimmer mit Badewanne",
        bathroom_with_window: "Bad mit Fenster",
        built_in_kitchen: "Einbauk\xFCche",
        cellar: "Keller",
        elevator: "Aufzug",
        free_of_commission: "Provisionsfrei",
        furnished: "Vollst\xE4ndig m\xF6bliert",
        garden: "Garten",
        no_attic_apartment: "Kein Dachgeschoss",
        no_ground_floor: "Kein Erdgeschoss",
        parking_garage: "Parkplatz/Garage",
        pets_allowed: "Haustiere erlaubt",
        reduced_mobility_access: "Barrierefrei",
        swimming_pool: "Schwimmbad",
        to_renovate: "Zu renovieren",
        underfloor: "Fu\xDFbodenheizung",
        unfurnished: "Unm\xF6bliert",
        vacant: "Frei/nicht vermietet"
      },
      subtitle: {
        accessibility: "Erreichbarkeit",
        furnished_unfurnished: "M\xF6bliert/unm\xF6bliert",
        indoor: "Innenbereich",
        outdoor: "Drau\xDFen",
        usage: "Verwendung"
      },
      title: {
        features_excluded: "Ausschlusskriterien",
        features_included: "Eigenschaften",
        subsidizing_housing: "Wohnberechtigungsschein"
      }
    },
    floors: {
      checkbox: {
        attic_floor: "Dachgeschoss",
        ground_floor: "Erdgeschoss",
        other_floors: "Andere Etage"
      },
      title: "Etage"
    },
    listings_display: {
      checkbox: {
        exclusive_mandate: "Exklusives Mandat",
        only_with_media: "Nur Anzeigen mit Fotos"
      },
      title: "Anzeigenanzeige"
    },
    living_area: {
      input_field: {
        surface_type: "m\xB2"
      },
      title: "Wohnfl\xE4che"
    },
    plot_area: {
      input_field: {
        surface_type: "m\xB2"
      },
      title: "Grundst\xFCcksfl\xE4che"
    },
    price_range: {
      title: "Preis"
    },
    project_type: {
      checkbox: {
        flat_sharing: "WG",
        foreclosure: "Zwangsversteigerung",
        investment: "Anlageobjekte",
        life_annuity: "Leibrente",
        new_builds: "Neubauimmobilien",
        projected: "Bauvorhaben",
        resale: "Wiederverkauf",
        stock: "Klassische Vermietung",
        swap_apartment: "Tauschwohnung",
        temporary_living: "Wohnen auf Zeit"
      },
      subtitle: {
        foreclosure: "Zwangsversteigerung",
        new_builds: "Neubauprojekte"
      },
      title: "Projekttyp"
    },
    provider: {
      checkbox: {
        private: "Privat",
        professional: "Gewerblich"
      },
      title: "Anbieter"
    },
    radius: {
      box: {
        location: {
          error_message: "Bitte geben Sie ein Ort an, um die Suche durchzuf\xFChren"
        }
      },
      button: {
        label: "Umkreissuche",
        title: "Alternative Suchoptionen"
      },
      buttongroup: {
        label: "Gebiet um Radius erweitern (in km)"
      },
      feedback: "Die Umkreissuche steht nur f\xFCr Suchen mit einem ausgew\xE4hlten Ort zur Verf\xFCgung. Bitte w\xE4hle nur einen Ort aus, um mit einem Umkreis zu suchen.",
      selected: {
        button: {
          label: "Umkreis anpassen"
        },
        title: "Umkreissuche"
      }
    },
    rooms_range: {
      dropdown: {
        any: "Egal"
      },
      title: "Zimmer"
    },
    show: {
      less: "Weniger anzeigen",
      more: "Mehr anzeigen"
    },
    subsidizing_housing: {
      button_group: {
        not_required: "Nicht notwendig",
        required: "Notwendig"
      }
    },
    travel_time: {
      box: {
        duration: {
          placeholder: "Dauer ausw\xE4hlen",
          title: "H\xF6chstdauer"
        },
        location: {
          error: {
            missing_address: "Bitte geben Sie eine Adresse ein, um fortzufahren"
          },
          error_message: "Bitte geben Sie eine Adresse an, um die Suche durchzuf\xFChren",
          placeholder: "Adresse eingeben (Arbeitsplatz, Schule usw.)",
          subtitle: "Geben Sie einen Ort ein, an dem Sie gerne in der N\xE4he wohnen w\xFCrden",
          title: "Adresse"
        },
        means: {
          bike: "Fahrrad",
          car: "Auto",
          title: "Transportmittel",
          transit: "\xD6ffentlicher Nahverkehr",
          walk: "Zu Fu\xDF"
        }
      },
      button: {
        label: "Suchen mit Pendelzeit"
      }
    }
  },
  filtersBar: {
    label: {
      project: "$t(filters.estate_type.checkbox.{{ estate_type }}) $t(filters.distribution_type.button_group.{{ distribution_type }})"
    },
    title: {
      filters: "Filter",
      livingArea: "Wohnfl\xE4che",
      location: "Standort",
      location_commute: "Benutzerdefinierter Bereich (Pendelzeit)",
      price: "Preis",
      project: "Projekt",
      rooms: "Zimmer"
    }
  },
  location: {
    autocomplete: {
      not_found: {
        subtitle: "Wir konnten keinen passenden Ort finden. Bitte \xFCberpr\xFCfe die Schreibweise oder versuche nach einer anderen Stadt, Postleitzahl oder Stadtteil zu suchen.",
        title: "Keine Ergebnisse"
      },
      suggestion: {
        suffix: {
          be: {
            borough: "Stadtbezirk",
            city: "Stadt",
            country: "Land",
            district: "Stadtteil",
            federal_state: "Bundesland",
            house_number: "Stra\xDFe",
            macro_neighborhood: "",
            postal_code: "Postleitzahl",
            province: "Landkreis"
          },
          borough: "Stadtbezirk",
          city: "Stadt",
          country: "Land",
          de: {
            borough: "Stadtbezirk",
            city: "Stadt",
            country: "Land",
            district: "Stadtteil",
            federal_state: "Bundesland",
            house_number: "Stra\xDFe",
            macro_neighborhood: "",
            postal_code: "Postleitzahl",
            province: "Landkreis"
          },
          default: {
            borough: "Stadtbezirk",
            city: "Stadt",
            country: "Land",
            district: "Stadtteil",
            federal_state: "Bundesland",
            house_number: "Stra\xDFe",
            macro_neighborhood: "",
            postal_code: "Postleitzahl",
            province: "Landkreis"
          },
          district: "Stadtteil",
          federalState: "Bundesland",
          fr: {
            borough: "Stadtbezirk",
            city: "Stadt",
            country: "Land",
            district: "Stadtteil",
            federal_state: "Bundesland",
            house_number: "Stra\xDFe",
            macro_neighborhood: "",
            postal_code: "Postleitzahl",
            province: "Landkreis"
          },
          house_number: "Stra\xDFe",
          province: "Landkreis"
        }
      },
      textfield: {
        aria_label: "Ort oder Postleitzahl eingeben",
        placeholder: {
          another: "Einen weiteren Standort hinzuf\xFCgen",
          empty: "Ort oder Postleitzahl eingeben"
        }
      }
    },
    button: {
      badge: {
        others_one: "(+{{count}} weitere)",
        others_other: "(+{{count}} weitere)"
      }
    },
    items: {
      districts: {
        label: "Bezirke / Stadtteile anpassen"
      },
      remove: {
        aria_label: "entfernen"
      },
      travel_time: {
        discard_aria_label: "Suche nach Reisezeit verwerfen",
        label: "Suche anpassen",
        title: "Reisezeitsuche"
      }
    }
  },
  modals: {
    districts: {
      checkbox_group: {
        all_boroughs: {
          label: "alle Stadtbezirke"
        },
        all_districts: {
          label: "alle Stadtteile"
        }
      },
      tabs: {
        boroughs: {
          label: "Stadtbezirke"
        },
        districts: {
          label: "Stadtteile"
        }
      },
      title: {
        default: "Stadtteile ausw\xE4hlen"
      }
    },
    filters: {
      title: {
        default: "Filter"
      }
    },
    location: {
      title: {
        default: "Suchbereich"
      }
    },
    radius: {
      title: {
        default: "Umkreissuche"
      }
    },
    saveButton: {
      label: "\xC4nderungen speichern"
    },
    submitButton: {
      placeholder: "Ergebnisse anzeigen",
      text_one: "{{count}} Ergebnis anzeigen",
      text_other: "{{count}} Ergebnisse anzeigen",
      text_interval: "(0-0)[0 Ergebnisse anzeigen];(9999-inf)[9999+ Ergebnisse anzeigen];"
    }
  }
};

// src/i18n/locales/en/refiner.json
var refiner_default2 = {
  filters: {
    area: {
      title: "Area"
    },
    bedrooms_range: {
      title: "Bedrooms"
    },
    common: {
      button_group: {
        all: "Show all",
        exclude: "Exclude",
        include: "Only relevant"
      },
      range: {
        input_field: {
          error_message: {
            max: "The maximum value must exceed the minimum.",
            min: "The minimum value must be less than the maximum."
          }
        },
        label: {
          max: "Max",
          min: "Min"
        }
      }
    },
    construction_year: {
      title: "Construction year"
    },
    context_switch: {
      commercial: "Business",
      residential: "Housing"
    },
    distribution_type: {
      button_group: {
        buy: "Buy",
        rent: "Rent"
      }
    },
    draw_on_map: {
      button: {
        label: "Draw search area"
      }
    },
    energy_performance: {
      subtitle: "Primary energy consumption (in kWh per m\xB2 per year)",
      title: "Energy Performance"
    },
    energy_type: {
      checkbox: {
        collective: "Collective heating",
        heatpump: "Heat pump",
        solar_electricity: "Solar panels"
      },
      title: "Energy & heating"
    },
    estate_subtype: {
      agriculture_forestry: {
        checkbox: {
          agriculture_company: "Agriculture company",
          farm_ranch: "Farm/Ranch",
          forestry: "Forestry",
          gardening_orchard: "Gardening/Orchard",
          hunting_fishing: "Hunting/Fishing",
          riding: "Riding",
          winery: "Winery"
        },
        title: "Type of estate"
      },
      apartment: {
        checkbox: {
          loft: "Loft",
          multi_storey: "Multi Storey",
          penthouse: "Penthouse",
          studio: "Studio",
          terrace: "Terrace"
        },
        title: "Type of apartment"
      },
      gastronomy_hotel: {
        checkbox: {
          cafe_bar_pub: "Caf\xE9/Bar/Pub",
          club_discotheque: "Club/Discotheque",
          hotel: "Hotel",
          hotel_pension: "Hotel",
          restaurant: "Restaurant"
        },
        title: "Type of estate"
      },
      house: {
        checkbox: {
          apartment_house: "Apartment house",
          bungalow: "Bungalow",
          castle: "Castle",
          chalet: "Chalet",
          farmhouse: "Farmhouse",
          finca: "Finca",
          manor_house: "Manor house",
          mansion: "Mansion",
          multi_family_house: "Multi family house",
          rustico: "Rustico",
          semidetached_house: "Semidetached house",
          single_family_house: "Single family house",
          terrace_house: "Terrace house",
          town_house: "Town house",
          villa: "Villa"
        },
        title: "Type of house"
      },
      office: {
        checkbox: {
          atelier: "Atelier",
          coworking: "Coworking",
          living_and_commercial_building: "Living and commercial building",
          medical: "Medical",
          office: "Office"
        },
        title: "Type of office"
      },
      parking: {
        checkbox: {
          carport: "Carport",
          duplex: "Duplex",
          garage: "Garage",
          outside: "Outside parking",
          underground_garage: "Underground garage",
          underground_parking_space: "Underground parking"
        },
        title: "Type of parking"
      },
      plot: {
        checkbox: {
          commercial: "Commercial",
          leisure_facility: "Leisure facility",
          living: "Living"
        },
        title: "Type of plot"
      },
      storage_production: {
        checkbox: {
          garage_repair: "Garage/Repair",
          logistics_center: "Logistics Center",
          misc_storage: "Miscellaneous storage",
          outdoor_space: "Outdoor space",
          production_hall: "Production hall",
          warehouse_hall: "Warehouse hall"
        },
        title: "Type of estate"
      },
      trading: {
        checkbox: {
          boutique: "Boutique",
          kiosk: "Kiosk",
          sales_area: "Sales area",
          shopping_centre: "Shopping center",
          showroom_space: "Showroom space",
          store: "Store"
        },
        title: "Type of shop"
      }
    },
    estate_type: {
      checkbox: {
        agriculture_forestry: "Agriculture/Forestry",
        apartment: "Apartment",
        commercial: "Commercial",
        gastronomy_hotel: "Gastronomy/Hotel",
        house: "House",
        house_apartment: "House or Apartment",
        investment: "Investment",
        miscellaneous: "Miscellaneous",
        new_builds: "New builds projects",
        office: "Office",
        parking: "Parking",
        plot: "Plot of land",
        shared_flats: "Shared flats",
        storage_production: "Storage/Production",
        temporary_living: "Temporary living",
        trading: "Shop"
      }
    },
    features: {
      checkbox: {
        balcony_terrace: "Balcony/Terrace",
        bathroom_with_bathtub: "Bathroom with bathtub",
        bathroom_with_window: "Bathroom with window",
        built_in_kitchen: "Built-in kitchen",
        cellar: "Cellar",
        elevator: "Elevator",
        free_of_commission: "Free of commission",
        furnished: "Fully furnished",
        garden: "Garden",
        no_attic_apartment: "No attic apartment",
        no_ground_floor: "No ground floor",
        parking_garage: "Parking/Garage",
        pets_allowed: "Pets allowed",
        reduced_mobility_access: "Reduced-mobility access",
        swimming_pool: "Swimming pool",
        to_renovate: "To renovate",
        underfloor: "Underfloor heating",
        unfurnished: "Unfurnished",
        vacant: "Vacant/Not rented"
      },
      subtitle: {
        accessibility: "Accessibility",
        furnished_unfurnished: "Furnished/Unfurnished",
        indoor: "Indoor",
        outdoor: "Outdoor",
        usage: "Usage"
      },
      title: {
        features_excluded: "Features to exclude",
        features_included: "Features",
        subsidizing_housing: "Subsidizing housing"
      }
    },
    floors: {
      checkbox: {
        attic_floor: "Attic apartment",
        ground_floor: "Ground floor",
        other_floors: "Other floors"
      },
      title: "Floor"
    },
    listings_display: {
      checkbox: {
        exclusive_mandate: "Exclusive mandate",
        only_with_media: "Only listings with photos"
      },
      title: "Listings display"
    },
    living_area: {
      input_field: {
        surface_type: "m\xB2"
      },
      title: "Living area"
    },
    plot_area: {
      input_field: {
        surface_type: "m\xB2"
      },
      title: "Plot area"
    },
    price_range: {
      title: "Price"
    },
    project_type: {
      checkbox: {
        flat_sharing: "Flat sharing",
        foreclosure: "Foreclosure sale",
        investment: "Investment",
        life_annuity: "Life annuity",
        new_builds: "New builds",
        projected: "Development project",
        resale: "Resale",
        stock: "Standard lease",
        swap_apartment: "Swap apartment",
        temporary_living: "Temporary living"
      },
      subtitle: {
        foreclosure: "Foreclosure",
        new_builds: "New builds"
      },
      title: "Project type"
    },
    provider: {
      checkbox: {
        private: "Private owner",
        professional: "Real estate agency"
      },
      title: "Provider"
    },
    radius: {
      box: {
        location: {
          error_message: "Please enter a location to perform the search"
        }
      },
      button: {
        label: "Search by radius",
        title: "Alternative Searches"
      },
      buttongroup: {
        label: "Extend area by radius (in km)"
      },
      feedback: "Searching by radius is not possible if multiple locations are selected. Please select only one location to use this search mode.",
      selected: {
        button: {
          label: "Adjust area"
        },
        title: "Radius search"
      }
    },
    rooms_range: {
      dropdown: {
        any: "Any"
      },
      title: "Rooms"
    },
    show: {
      less: "Show less",
      more: "Show more"
    },
    subsidizing_housing: {
      button_group: {
        not_required: "Not required",
        required: "Required"
      }
    },
    travel_time: {
      box: {
        duration: {
          placeholder: "Select duration",
          title: "Max. duration"
        },
        location: {
          error: {
            missing_address: "Please enter an address to proceed"
          },
          error_message: "Please enter an address to perform the search",
          placeholder: "Enter an address (workplace, school, etc.)",
          subtitle: "Enter a place you'd like to live nearby",
          title: "Address"
        },
        means: {
          bike: "Biking",
          car: "Driving",
          title: "Transportation",
          transit: "Transit",
          walk: "Walking"
        }
      },
      button: {
        label: "Search by commute time"
      }
    }
  },
  filtersBar: {
    label: {
      project: "$t(filters.distribution_type.button_group.{{ distribution_type }}) $t(filters.estate_type.checkbox.{{ estate_type }})"
    },
    title: {
      filters: "Filters",
      livingArea: "Living area",
      location: "Location",
      location_commute: "Custom area (commute time)",
      price: "Price",
      project: "Project",
      rooms: "Rooms"
    }
  },
  location: {
    autocomplete: {
      not_found: {
        subtitle: "We were not able to find any location with this input. Please check the spelling or try searching for another location or zip code.",
        title: "No matching location found"
      },
      suggestion: {
        suffix: {
          be: {
            borough: "Borough",
            city: "City",
            country: "Country",
            district: "District",
            federal_state: "Federal State/Region",
            house_number: "Street",
            macro_neighborhood: "",
            postal_code: "Postcode",
            province: "Province"
          },
          borough: "Borough",
          city: "City",
          country: "Country",
          de: {
            borough: "Borough",
            city: "City",
            country: "Country",
            district: "District",
            federal_state: "Federal State/Region",
            house_number: "Street",
            macro_neighborhood: "",
            postal_code: "Postcode",
            province: "Province"
          },
          default: {
            borough: "Borough",
            city: "City",
            country: "Country",
            district: "District",
            federal_state: "Federal State/Region",
            house_number: "Street",
            macro_neighborhood: "",
            postal_code: "Postcode",
            province: "Province"
          },
          district: "District",
          federalState: "Federal State/Region",
          fr: {
            borough: "Borough",
            city: "City",
            country: "Country",
            district: "District",
            federal_state: "Federal State/Region",
            house_number: "Street",
            macro_neighborhood: "",
            postal_code: "Postcode",
            province: "Province"
          },
          house_number: "Street",
          province: "Province"
        }
      },
      textfield: {
        aria_label: "Enter location or zip code",
        placeholder: {
          another: "Add another location",
          empty: "Enter location or zip code"
        }
      }
    },
    button: {
      badge: {
        others_one: "(+{{count}} more)",
        others_other: "(+{{count}} more)"
      }
    },
    items: {
      districts: {
        label: "Adjust districts"
      },
      remove: {
        aria_label: "Remove"
      },
      travel_time: {
        discard_aria_label: "Discard travel time search",
        label: "Adjust search",
        title: "Search by travel time"
      }
    }
  },
  modals: {
    districts: {
      checkbox_group: {
        all_boroughs: {
          label: "All boroughs"
        },
        all_districts: {
          label: "All districts"
        }
      },
      tabs: {
        boroughs: {
          label: "Boroughs"
        },
        districts: {
          label: "Districts"
        }
      },
      title: {
        default: "Select districts"
      }
    },
    filters: {
      title: {
        default: "Filters"
      }
    },
    location: {
      title: {
        default: "Search area"
      }
    },
    radius: {
      title: {
        default: "Radius Search"
      }
    },
    saveButton: {
      label: "Save changes"
    },
    submitButton: {
      placeholder: "Show results",
      text_one: "Show {{count}} result",
      text_other: "Show {{count}} results",
      text_interval: "(0-0)[Show 0 results];(9999-inf)[Show 9999+ results];"
    }
  }
};

// src/i18n/locales/fr/refiner.json
var refiner_default3 = {
  filters: {
    area: {
      title: "Surface"
    },
    bedrooms_range: {
      title: "Chambres"
    },
    common: {
      button_group: {
        all: "Tout afficher",
        exclude: "Exclure",
        include: "Uniquement"
      },
      range: {
        input_field: {
          error_message: {
            max: "La valeur maximale doit \xEAtre sup\xE9rieure \xE0 la minimale.",
            min: "La valeur minimale doit \xEAtre inf\xE9rieure \xE0 la maximale."
          }
        },
        label: {
          max: "Maximum",
          min: "Minimum"
        }
      }
    },
    construction_year: {
      title: "Ann\xE9e de construction"
    },
    context_switch: {
      commercial: "Pour mon entreprise",
      residential: "Pour moi"
    },
    distribution_type: {
      button_group: {
        buy: "Acheter",
        rent: "Louer"
      }
    },
    draw_on_map: {
      button: {
        label: "Dessiner la zone de recherche"
      }
    },
    energy_performance: {
      subtitle: "Consommation d'\xE9nergie primaire (en kWh par m\xB2 et par an)",
      title: "Performance \xE9nerg\xE9tique"
    },
    energy_type: {
      checkbox: {
        collective: "Chauffage collectif",
        heatpump: "Pompe \xE0 chaleur",
        solar_electricity: "Panneaux solaires"
      },
      title: "\xC9nergie et chauffage"
    },
    estate_subtype: {
      agriculture_forestry: {
        checkbox: {
          agriculture_company: "Entreprise agricole",
          farm_ranch: "Ferme/ranch",
          forestry: "For\xEAts",
          gardening_orchard: "Jardinage/verger",
          hunting_fishing: "Chasse/p\xEAche",
          riding: "\xC9questre",
          winery: "Vignobles"
        },
        title: "Type de bien"
      },
      apartment: {
        checkbox: {
          loft: "Loft",
          multi_storey: "Appartement \xE0 plusieurs \xE9tages",
          penthouse: "Appartement-terrasse",
          studio: "Studio",
          terrace: "Terrasse"
        },
        title: "Type d'appartement"
      },
      gastronomy_hotel: {
        checkbox: {
          cafe_bar_pub: "Caf\xE9/bar/pub",
          club_discotheque: "Club/discoth\xE8que",
          hotel: "H\xF4tel",
          hotel_pension: "H\xF4tel",
          restaurant: "Restaurant"
        },
        title: "Type de bien"
      },
      house: {
        checkbox: {
          apartment_house: "Immeuble",
          bungalow: "Maison de plain-pied",
          castle: "Ch\xE2teau",
          chalet: "Chalet",
          farmhouse: "Ferme",
          finca: "Maison de campagne",
          manor_house: "Demeure",
          mansion: "H\xF4tel particulier",
          multi_family_house: "Maison multifamiliale",
          rustico: "Rustico",
          semidetached_house: "Maison jumel\xE9e",
          single_family_house: "Maison individuelle",
          terrace_house: "Maison jumel\xE9e",
          town_house: "Maison de ville",
          villa: "Villa"
        },
        title: "Type de maison"
      },
      office: {
        checkbox: {
          atelier: "Atelier",
          coworking: "Espace de coworking",
          living_and_commercial_building: "B\xE2timent d'habitation et commercial",
          medical: "M\xE9dical",
          office: "Bureau"
        },
        title: "Type de bureau"
      },
      parking: {
        checkbox: {
          carport: "Abri pour voiture",
          duplex: "Garage double",
          garage: "Garage",
          outside: "Parking ext\xE9rieur",
          underground_garage: "Garage souterrain",
          underground_parking_space: "Parking souterrain"
        },
        title: "Type de parking"
      },
      plot: {
        checkbox: {
          commercial: "Commercial",
          leisure_facility: "Centre de loisirs",
          living: "R\xE9sidentiel"
        },
        title: "Type de terrain"
      },
      storage_production: {
        checkbox: {
          garage_repair: "Garage/r\xE9paration",
          logistics_center: "Centre logistique",
          misc_storage: "Stockage divers",
          outdoor_space: "Espace ext\xE9rieur",
          production_hall: "Local d'activit\xE9s",
          warehouse_hall: "Entrep\xF4t"
        },
        title: "Type de bien"
      },
      trading: {
        checkbox: {
          boutique: "Boutique",
          kiosk: "Kiosque",
          sales_area: "Espace de vente",
          shopping_centre: "Centre commercial",
          showroom_space: "Espace d'exposition",
          store: "Local commercial"
        },
        title: "Type de local / boutique"
      }
    },
    estate_type: {
      checkbox: {
        agriculture_forestry: "Agriculture et for\xEAts",
        apartment: "Appartement",
        commercial: "Commercial",
        gastronomy_hotel: "Caf\xE9 / H\xF4tel / Restaurant",
        house: "Maison",
        house_apartment: "Maison ou appartement",
        investment: "Investissement immobilier",
        miscellaneous: "Divers",
        new_builds: "Immobilier neuf",
        office: "Bureau",
        parking: "Parking",
        plot: "Terrain",
        shared_flats: "Colocations",
        storage_production: "Local d\u2019activit\xE9 / Entrep\xF4t",
        temporary_living: "Logement temporaire",
        trading: "Local commercial / Boutique"
      }
    },
    features: {
      checkbox: {
        balcony_terrace: "Balcon/terrasse",
        bathroom_with_bathtub: "Salle de bain avec baignoire",
        bathroom_with_window: "Salle de bain avec fen\xEAtre",
        built_in_kitchen: "Cuisine int\xE9gr\xE9e",
        cellar: "Cave",
        elevator: "Ascenseur",
        free_of_commission: "Sans frais d'agence",
        furnished: "Enti\xE8rement meubl\xE9",
        garden: "Jardin",
        no_attic_apartment: "",
        no_ground_floor: "",
        parking_garage: "Parking/garage",
        pets_allowed: "Animaux autoris\xE9s",
        reduced_mobility_access: "Acc\xE8s \xE0 mobilit\xE9 r\xE9duite",
        swimming_pool: "Piscine",
        to_renovate: "\xC0 r\xE9nover",
        underfloor: "Chauffage au sol",
        unfurnished: "Non meubl\xE9",
        vacant: "Libre/non lou\xE9"
      },
      subtitle: {
        accessibility: "Accessibilit\xE9",
        furnished_unfurnished: "Meubl\xE9/non meubl\xE9",
        indoor: "Int\xE9rieur",
        outdoor: "Ext\xE9rieur",
        usage: "Utilisation"
      },
      title: {
        features_excluded: "Caract\xE9ristiques \xE0 exclure",
        features_included: "Caract\xE9ristiques",
        subsidizing_housing: "Logement subventionn\xE9"
      }
    },
    floors: {
      checkbox: {
        attic_floor: "Sous les toits",
        ground_floor: "Rez-de-chauss\xE9e",
        other_floors: "Autres \xE9tages"
      },
      title: "\xC9tage"
    },
    listings_display: {
      checkbox: {
        exclusive_mandate: "Exclusivit\xE9 de l'agence",
        only_with_media: "Annonces avec photos uniquement"
      },
      title: "Affichage des annonces"
    },
    living_area: {
      input_field: {
        surface_type: "m\xB2"
      },
      title: "Surface"
    },
    plot_area: {
      input_field: {
        surface_type: "m\xB2"
      },
      title: "Surface du terrain"
    },
    price_range: {
      title: "Budget"
    },
    project_type: {
      checkbox: {
        flat_sharing: "Colocation",
        foreclosure: "Vente aux ench\xE8res",
        investment: "Investissement immobilier",
        life_annuity: "Vente en viager",
        new_builds: "Immobilier neuf",
        projected: "Projet de construction",
        resale: "Ancien",
        stock: "Location classique",
        swap_apartment: "\xC9change d'appartement",
        temporary_living: "Logement temporaire"
      },
      subtitle: {
        foreclosure: "Vente aux ench\xE8res",
        new_builds: "Immobilier neuf"
      },
      title: "Type de projet"
    },
    provider: {
      checkbox: {
        private: "Particuliers",
        professional: "Professionnels"
      },
      title: "Propos\xE9 par"
    },
    radius: {
      box: {
        location: {
          error_message: "Veuillez renseigner un lieu pour effectuer la recherche"
        }
      },
      button: {
        label: "Recherche par p\xE9rim\xE8tre",
        title: "Recherches alternatives"
      },
      buttongroup: {
        label: "\xC9tendre la zone par distance (en km)"
      },
      feedback: "La recherche par p\xE9rim\xE8tre n'est pas possible si plusieurs lieux sont s\xE9lectionn\xE9s. Veuillez ne s\xE9lectionner qu'un seul lieu pour utiliser cette recherche.",
      selected: {
        button: {
          label: "Ajuster la zone"
        },
        title: "Recherche par p\xE9rim\xE8tre"
      }
    },
    rooms_range: {
      dropdown: {
        any: "-"
      },
      title: "Pi\xE8ces"
    },
    show: {
      less: "Voir moins",
      more: "Voir plus"
    },
    subsidizing_housing: {
      button_group: {
        not_required: "Pas n\xE9cessaire",
        required: "Exig\xE9"
      }
    },
    travel_time: {
      box: {
        duration: {
          placeholder: "S\xE9lectionner la dur\xE9e",
          title: "Dur\xE9e maximale"
        },
        location: {
          error: {
            missing_address: "Veuillez saisir une adresse pour continuer"
          },
          error_message: "Veuillez renseigner une adresse pour effectuer la recherche",
          placeholder: "Saisir une adresse (lieu de travail, \xE9cole, etc.)",
          subtitle: "Indiquez un lieu proche duquel vous aimeriez vivre",
          title: "Adresse"
        },
        means: {
          bike: "V\xE9lo",
          car: "Voiture",
          title: "Moyen de transport",
          transit: "Transports en commun",
          walk: "\xC0 pied"
        }
      },
      button: {
        label: "Recherche par temps de trajet"
      }
    }
  },
  filtersBar: {
    label: {
      project: "$t(filters.distribution_type.button_group.{{ distribution_type }}) $t(filters.estate_type.checkbox.{{ estate_type }})"
    },
    title: {
      filters: "Filtres",
      livingArea: "Surfaces",
      location: "Localisation",
      location_commute: "Zone personnalis\xE9e (temps de trajet)",
      price: "Budget",
      project: "Projet",
      rooms: "Pi\xE8ces"
    }
  },
  location: {
    autocomplete: {
      not_found: {
        subtitle: "Nous n'avons pas pu trouver de lieu correspondant \xE0 votre saisie. Veuillez v\xE9rifier l'orthographe ou essayer de rechercher un autre lieu ou code postal.",
        title: "Aucun lieu correspondant n'a \xE9t\xE9 trouv\xE9"
      },
      suggestion: {
        suffix: {
          be: {
            borough: "Arrondissement",
            city: "Ville",
            country: "Pays",
            district: "Quartier",
            federal_state: "R\xE9gion",
            house_number: "Rue",
            macro_neighborhood: "",
            postal_code: "Code postal",
            province: "Province"
          },
          borough: "Arrondissement",
          city: "Ville",
          country: "Pays",
          de: {
            borough: "Arrondissement",
            city: "Ville",
            country: "Pays",
            district: "Quartier",
            federal_state: "R\xE9gion",
            house_number: "Rue",
            macro_neighborhood: "",
            postal_code: "Code postal",
            province: "D\xE9partement"
          },
          default: {
            borough: "Arrondissement",
            city: "Ville",
            country: "Pays",
            district: "Quartier",
            federal_state: "R\xE9gion",
            house_number: "Rue",
            macro_neighborhood: "",
            postal_code: "Code postal",
            province: "D\xE9partement"
          },
          district: "Quartier",
          federalState: "R\xE9gion",
          fr: {
            borough: "Arrondissement",
            city: "Ville",
            country: "Pays",
            district: "Quartier",
            federal_state: "R\xE9gion",
            house_number: "Rue",
            macro_neighborhood: "",
            postal_code: "Code postal",
            province: "D\xE9partement"
          },
          house_number: "Rue",
          province: "D\xE9partement"
        }
      },
      textfield: {
        aria_label: "Saisir le lieu ou le code postal",
        placeholder: {
          another: "Ajouter un autre lieu",
          empty: "Saisir le lieu ou le code postal"
        }
      }
    },
    button: {
      badge: {
        others_one: "(+{{count}} autre)",
        others_other: "(+{{count}} autres)"
      }
    },
    items: {
      districts: {
        label: "S\xE9lectionner les quartiers"
      },
      remove: {
        aria_label: "Retirer"
      },
      travel_time: {
        discard_aria_label: "Abandon de la recherche sur les temps de trajet",
        label: "Ajuster la recherche",
        title: "Recherche par temps de trajet"
      }
    }
  },
  modals: {
    districts: {
      checkbox_group: {
        all_boroughs: {
          label: "tous les arrondissements"
        },
        all_districts: {
          label: "tous les quartiers"
        }
      },
      tabs: {
        boroughs: {
          label: "Arrondissements"
        },
        districts: {
          label: "Quartiers"
        }
      },
      title: {
        default: "S\xE9lectionner les quartiers"
      }
    },
    filters: {
      title: {
        default: "Filtres"
      }
    },
    location: {
      title: {
        default: "Zone de recherche"
      }
    },
    radius: {
      title: {
        default: "Recherche par p\xE9rim\xE8tre"
      }
    },
    saveButton: {
      label: "Enregistrer les modifications"
    },
    submitButton: {
      placeholder: "Afficher les annonces",
      text_one: "Afficher {{count}} annonce",
      text_other: "Afficher {{count}} annonces",
      text_interval: "(0-0)[Afficher 0 annonce];(9999-inf)[Afficher 9999+ annonces];"
    }
  }
};

// src/i18n/index.ts
var resources = {
  en: {
    refiner: refiner_default2
  },
  de: {
    refiner: refiner_default
  },
  fr: {
    refiner: refiner_default3
  }
};
var i18n = i18next.createInstance();
void i18n.use(intervalPlural).init({
  defaultNS: "refiner",
  resources,
  lng: "en",
  interpolation: {
    escapeValue: false
  }
});
var i18n_default = i18n;

// src/helpers/hooks/useTranslate.ts
var useTranslate = (key, nestingOptions = {}) => {
  const { t } = i18n_default;
  return t(key, nestingOptions);
};
var fetchRelations = async (placeId, serviceUrl, placeType) => {
  const config = {
    params: { placeId, placeType }
  };
  const url = `${serviceUrl}/places/relations`;
  const result = await axios.get(url, config).catch(() => ({ data: { relations: [] } }));
  return result?.data?.relations ?? [];
};

// src/helpers/hooks/useRelation.ts
var useRelation = (cityId, placeType) => {
  const [districtsOrBoroughs = [], setDistrictsOrBoroughs] = useState([]);
  const serviceUrl = useServiceUrl();
  useEffect(() => {
    void (async () => {
      try {
        const data = await fetchRelations(cityId, serviceUrl, placeType);
        const result = data.sort((a, b) => a.label.localeCompare(b.label)).map(({ label, placeId, coordinates, postal_codes }) => ({
          id: placeId,
          labels: [label],
          coordinates,
          postalCodes: postal_codes
        }));
        setDistrictsOrBoroughs(result);
      } catch {
      }
    })();
  }, []);
  return districtsOrBoroughs;
};

// src/helpers/hooks/useCountUrl.ts
var useCountUrl = () => {
  const countMode = useCountMode();
  let searchType = useSearchType();
  const serviceUrl = useServiceUrl();
  searchType = searchType === "project" ? "project" : "classified";
  const urls = {
    project: {
      geo: [serviceUrl, "project", "count"].join("/"),
      default: [serviceUrl, "project", "count"].join("/")
    },
    classified: {
      geo: [serviceUrl, "geo", "count"].join("/"),
      default: [serviceUrl, "count"].join("/")
    }
  };
  return urls[searchType][countMode];
};
var fetchCount = async (model, url) => {
  const result = await axios.post(url, model).catch(() => ({ data: { totalCount: 0 } }));
  return result?.data?.totalCount;
};

// src/helpers/context/SearchContext/useUpdateCount.tsx
var useUpdateCount = ({ setCount, setIsValid, setInProgress }) => {
  const countUrl = useCountUrl();
  const templates = useTemplates();
  const model = useWatch();
  const space = useWatch({ name: "space" });
  const plotSpace = useWatch({ name: "plotSpace" });
  const yearOfConstruction = useWatch({ name: "yearOfConstruction" });
  const actualModel = { ...model, space, plotSpace, yearOfConstruction };
  const searchServiceModel = mapFormModelToSearchServiceModel(actualModel, templates);
  useDebounce(
    async () => {
      if (!isModelValid(searchServiceModel)) {
        setCount(0);
        setIsValid(false);
        setInProgress(false);
        return;
      }
      setIsValid(true);
      setInProgress(true);
      const count = await fetchCount(searchServiceModel, countUrl);
      setInProgress(false);
      setCount(count ?? 0);
    },
    250,
    [JSON.stringify(searchServiceModel)]
  );
};

// src/brands/classifieds/de/energyCertificateOptions.ts
var EnergyCertificateClasses = [
  { value: "A_PLUS", label: "A+", barLabel: "< 30", size: 0, color: "color.scales.energy.green200" },
  { value: "A", barLabel: "30 - 49", size: 30, color: "color.scales.energy.green300" },
  { value: "B", barLabel: "50 - 74", size: 50, color: "color.scales.energy.green400" },
  { value: "C", barLabel: "75 - 99", size: 75, color: "color.scales.energy.yellow100" },
  { value: "D", barLabel: "100 - 129", size: 100, color: "color.scales.energy.yellow200" },
  { value: "E", barLabel: "130 - 159", size: 130, color: "color.scales.energy.orange100" },
  { value: "F", barLabel: "160 - 199", size: 160, color: "color.scales.energy.orange200" },
  { value: "G", barLabel: "200 - 250", size: 200, color: "color.scales.energy.red100" },
  { value: "H", barLabel: "> 250", size: 250, color: "color.scales.energy.red200" }
];
var EstateSubTypeOptions = {
  [FormEstateType.HOUSE_APARTMENT]: [],
  [FormEstateType.HOUSE]: [
    FormEstateSubType.BUNGALOW,
    FormEstateSubType.CASTLE,
    FormEstateSubType.FARMHOUSE,
    FormEstateSubType.FINCA,
    FormEstateSubType.MANOR_HOUSE,
    FormEstateSubType.MULTI_FAMILY_HOUSE,
    FormEstateSubType.RUSTICO,
    FormEstateSubType.SEMIDETACHED_HOUSE,
    FormEstateSubType.SINGLE_FAMILY_HOUSE,
    FormEstateSubType.TERRACE_HOUSE,
    FormEstateSubType.TOWN_HOUSE,
    FormEstateSubType.VILLA
  ],
  [FormEstateType.APARTMENT]: [
    FormEstateSubType.LOFT,
    FormEstateSubType.MULTI_STOREY,
    FormEstateSubType.PENTHOUSE,
    FormEstateSubType.STUDIO,
    FormEstateSubType.TERRACE
  ],
  [FormEstateType.PARKING]: [
    FormEstateSubType.CARPORT,
    FormEstateSubType.DUPLEX,
    FormEstateSubType.GARAGE,
    FormEstateSubType.OUTSIDE,
    FormEstateSubType.UNDERGROUND_GARAGE
  ],
  [FormEstateType.PLOT]: [FormEstateSubType.COMMERCIAL, FormEstateSubType.LEISURE_FACILITY, FormEstateSubType.LIVING],
  [FormEstateType.AGRICULTURE_FORESTRY]: [
    FormEstateSubType.AGRICULTURE_COMPANY,
    FormEstateSubType.FARM_RANCH,
    FormEstateSubType.FORESTRY,
    FormEstateSubType.GARDENING_ORCHARD,
    FormEstateSubType.HUNTING_FISHING,
    FormEstateSubType.RIDING,
    FormEstateSubType.WINERY
  ],
  [FormEstateType.GASTRONOMY_HOTEL]: [
    FormEstateSubType.CAFE_BAR_PUB,
    FormEstateSubType.CLUB_DISCOTHEQUE,
    FormEstateSubType.HOTEL_PENSION,
    FormEstateSubType.RESTAURANT
  ],
  [FormEstateType.OFFICE]: [
    FormEstateSubType.ATELIER,
    FormEstateSubType.COWORKING,
    FormEstateSubType.LIVING_AND_COMMERCIAL_BUILDING,
    FormEstateSubType.MEDICAL,
    FormEstateSubType.OFFICE
  ],
  [FormEstateType.STORAGE_PRODUCTION]: [
    FormEstateSubType.GARAGE_REPAIR,
    FormEstateSubType.LOGISTICS_CENTER,
    FormEstateSubType.MISC_STORAGE,
    FormEstateSubType.OUTDOOR_SPACE,
    FormEstateSubType.PRODUCTION_HALL,
    FormEstateSubType.WAREHOUSE_HALL
  ],
  [FormEstateType.TRADING]: [
    FormEstateSubType.KIOSK,
    FormEstateSubType.SALES_AREA,
    FormEstateSubType.SHOPPING_CENTRE,
    FormEstateSubType.SHOWROOM_SPACE,
    FormEstateSubType.STORE
  ],
  [FormEstateType.MISCELLANEOUS]: []
};
var FeatureOptions = {
  [UsageType.RESIDENTIAL]: {
    [FormDistributionType.BUY]: {
      [FormEstateType.HOUSE_APARTMENT]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.GARDEN,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.KITCHEN_FULLY_EQUIPPED,
        FormFeatureType.BATHROOM_WINDOW,
        FormFeatureType.BATHROOM_BATHTUB,
        FormFeatureType.CELLAR,
        FormFeatureType.VACANT,
        FormFeatureType.FURNISHED,
        FormFeatureType.UNFURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.COMMISSION_FREE,
        FormFeatureType.NEED_OF_RENOVATION,
        FormFeatureType.SWIMMING_POOL,
        FormFeatureType.UNDERFLOOR
      ],
      [FormEstateType.HOUSE]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.GARDEN,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.KITCHEN_FULLY_EQUIPPED,
        FormFeatureType.BATHROOM_WINDOW,
        FormFeatureType.BATHROOM_BATHTUB,
        FormFeatureType.CELLAR,
        FormFeatureType.VACANT,
        FormFeatureType.FURNISHED,
        FormFeatureType.UNFURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.COMMISSION_FREE,
        FormFeatureType.NEED_OF_RENOVATION,
        FormFeatureType.SWIMMING_POOL,
        FormFeatureType.UNDERFLOOR
      ],
      [FormEstateType.APARTMENT]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.GARDEN,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.KITCHEN_FULLY_EQUIPPED,
        FormFeatureType.BATHROOM_WINDOW,
        FormFeatureType.BATHROOM_BATHTUB,
        FormFeatureType.CELLAR,
        FormFeatureType.VACANT,
        FormFeatureType.FURNISHED,
        FormFeatureType.UNFURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.NEED_OF_RENOVATION,
        FormFeatureType.COMMISSION_FREE,
        FormFeatureType.SWIMMING_POOL,
        FormFeatureType.UNDERFLOOR
      ],
      [FormEstateType.MISCELLANEOUS]: [FormFeatureType.COMMISSION_FREE, FormFeatureType.NEED_OF_RENOVATION],
      [FormEstateType.PARKING]: [FormFeatureType.COMMISSION_FREE],
      [FormEstateType.PLOT]: [FormFeatureType.COMMISSION_FREE],
      [FormEstateType.OFFICE]: [],
      [FormEstateType.TRADING]: [],
      [FormEstateType.GASTRONOMY_HOTEL]: [],
      [FormEstateType.STORAGE_PRODUCTION]: [],
      [FormEstateType.AGRICULTURE_FORESTRY]: []
    },
    [FormDistributionType.RENT]: {
      [FormEstateType.HOUSE_APARTMENT]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.GARDEN,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.KITCHEN_FULLY_EQUIPPED,
        FormFeatureType.BATHROOM_WINDOW,
        FormFeatureType.BATHROOM_BATHTUB,
        FormFeatureType.CELLAR,
        FormFeatureType.VACANT,
        FormFeatureType.PETS_ALLOWED,
        FormFeatureType.FURNISHED,
        FormFeatureType.UNFURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.COMMISSION_FREE,
        FormFeatureType.UNDERFLOOR
      ],
      [FormEstateType.HOUSE]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.GARDEN,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.KITCHEN_FULLY_EQUIPPED,
        FormFeatureType.BATHROOM_WINDOW,
        FormFeatureType.BATHROOM_BATHTUB,
        FormFeatureType.CELLAR,
        FormFeatureType.VACANT,
        FormFeatureType.PETS_ALLOWED,
        FormFeatureType.FURNISHED,
        FormFeatureType.UNFURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.UNDERFLOOR
      ],
      [FormEstateType.APARTMENT]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.GARDEN,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.KITCHEN_FULLY_EQUIPPED,
        FormFeatureType.BATHROOM_WINDOW,
        FormFeatureType.BATHROOM_BATHTUB,
        FormFeatureType.CELLAR,
        FormFeatureType.VACANT,
        FormFeatureType.PETS_ALLOWED,
        FormFeatureType.FURNISHED,
        FormFeatureType.UNFURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.UNDERFLOOR
      ],
      [FormEstateType.PLOT]: [],
      [FormEstateType.PARKING]: [],
      [FormEstateType.MISCELLANEOUS]: [FormFeatureType.VACANT],
      [FormEstateType.OFFICE]: [],
      [FormEstateType.TRADING]: [],
      [FormEstateType.GASTRONOMY_HOTEL]: [],
      [FormEstateType.STORAGE_PRODUCTION]: [],
      [FormEstateType.AGRICULTURE_FORESTRY]: []
    }
  },
  [UsageType.COMMERCIAL]: {
    [FormDistributionType.BUY]: {
      [FormEstateType.OFFICE]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.CELLAR,
        FormFeatureType.FURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.COMMISSION_FREE,
        FormFeatureType.NEED_OF_RENOVATION
      ],
      [FormEstateType.STORAGE_PRODUCTION]: [
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.COMMISSION_FREE
      ],
      [FormEstateType.AGRICULTURE_FORESTRY]: [
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.COMMISSION_FREE
      ],
      [FormEstateType.TRADING]: [
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.CELLAR,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.COMMISSION_FREE
      ],
      [FormEstateType.GASTRONOMY_HOTEL]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.CELLAR,
        FormFeatureType.FURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.COMMISSION_FREE
      ],
      [FormEstateType.PLOT]: [FormFeatureType.COMMISSION_FREE],
      [FormEstateType.PARKING]: [FormFeatureType.REDUCE_MOBILITY_ACCESS, FormFeatureType.COMMISSION_FREE],
      [FormEstateType.MISCELLANEOUS]: [FormFeatureType.COMMISSION_FREE],
      [FormEstateType.HOUSE_APARTMENT]: [],
      [FormEstateType.HOUSE]: [],
      [FormEstateType.APARTMENT]: []
    },
    [FormDistributionType.RENT]: {
      [FormEstateType.OFFICE]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.CELLAR,
        FormFeatureType.FURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.COMMISSION_FREE
      ],
      [FormEstateType.STORAGE_PRODUCTION]: [
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.COMMISSION_FREE
      ],
      [FormEstateType.AGRICULTURE_FORESTRY]: [
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.COMMISSION_FREE
      ],
      [FormEstateType.TRADING]: [
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.CELLAR,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.COMMISSION_FREE
      ],
      [FormEstateType.GASTRONOMY_HOTEL]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.CELLAR,
        FormFeatureType.FURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.COMMISSION_FREE
      ],
      [FormEstateType.PLOT]: [FormFeatureType.COMMISSION_FREE],
      [FormEstateType.PARKING]: [FormFeatureType.REDUCE_MOBILITY_ACCESS, FormFeatureType.COMMISSION_FREE],
      [FormEstateType.MISCELLANEOUS]: [FormFeatureType.COMMISSION_FREE],
      [FormEstateType.HOUSE_APARTMENT]: [],
      [FormEstateType.HOUSE]: [],
      [FormEstateType.APARTMENT]: []
    }
  }
};
var FilterTypeOptions = {
  [UsageType.RESIDENTIAL]: {
    [FormDistributionType.BUY]: {
      [FormEstateType.HOUSE_APARTMENT]: {
        showRoomsFilter: true,
        showSpaceFilter: true,
        showYearofConstructionFilter: true,
        showFloorsFilter: true,
        showProviderTypeFilter: true,
        showEnergyCertificate: true,
        showPlotFilter: true,
        showEnergyTypeFilter: true
      },
      [FormEstateType.HOUSE]: {
        showRoomsFilter: true,
        showSpaceFilter: true,
        showPlotFilter: true,
        showYearofConstructionFilter: true,
        showProviderTypeFilter: true,
        showEnergyCertificate: true,
        showEnergyTypeFilter: true
      },
      [FormEstateType.APARTMENT]: {
        showRoomsFilter: true,
        showSpaceFilter: true,
        showYearofConstructionFilter: true,
        showFloorsFilter: true,
        showProviderTypeFilter: true,
        showEnergyCertificate: true,
        showEnergyTypeFilter: true
      },
      [FormEstateType.MISCELLANEOUS]: {
        showRoomsFilter: true,
        showPlotFilter: true,
        showSpaceFilter: true,
        showYearofConstructionFilter: true,
        showProviderTypeFilter: true,
        showEnergyCertificate: true
      },
      [FormEstateType.PARKING]: {
        showProviderTypeFilter: true
      },
      [FormEstateType.PLOT]: {
        showPlotFilter: true,
        showProviderTypeFilter: true
      },
      [FormEstateType.OFFICE]: {},
      [FormEstateType.TRADING]: {},
      [FormEstateType.GASTRONOMY_HOTEL]: {},
      [FormEstateType.STORAGE_PRODUCTION]: {},
      [FormEstateType.AGRICULTURE_FORESTRY]: {}
    },
    [FormDistributionType.RENT]: {
      [FormEstateType.HOUSE_APARTMENT]: {
        showRoomsFilter: true,
        showSpaceFilter: true,
        showYearofConstructionFilter: true,
        showFloorsFilter: true,
        showProviderTypeFilter: true,
        showEnergyCertificate: true,
        showSubsidizingHousing: true,
        showPlotFilter: true,
        showEnergyTypeFilter: true
      },
      [FormEstateType.HOUSE]: {
        showRoomsFilter: true,
        showSpaceFilter: true,
        showPlotFilter: true,
        showYearofConstructionFilter: true,
        showProviderTypeFilter: true,
        showEnergyCertificate: true,
        showSubsidizingHousing: true,
        showEnergyTypeFilter: true
      },
      [FormEstateType.APARTMENT]: {
        showRoomsFilter: true,
        showSpaceFilter: true,
        showFloorsFilter: true,
        showYearofConstructionFilter: true,
        showProviderTypeFilter: true,
        showEnergyCertificate: true,
        showSubsidizingHousing: true,
        showEnergyTypeFilter: true
      },
      [FormEstateType.PLOT]: {
        showPlotFilter: true,
        showProviderTypeFilter: true
      },
      [FormEstateType.PARKING]: {
        showProviderTypeFilter: true
      },
      [FormEstateType.MISCELLANEOUS]: {
        showRoomsFilter: true,
        showPlotFilter: true,
        showSpaceFilter: true,
        showYearofConstructionFilter: true,
        showProviderTypeFilter: true,
        showEnergyCertificate: true
      },
      [FormEstateType.OFFICE]: {},
      [FormEstateType.TRADING]: {},
      [FormEstateType.GASTRONOMY_HOTEL]: {},
      [FormEstateType.STORAGE_PRODUCTION]: {},
      [FormEstateType.AGRICULTURE_FORESTRY]: {}
    }
  },
  [UsageType.COMMERCIAL]: {
    [FormDistributionType.BUY]: {
      [FormEstateType.OFFICE]: {
        showSpaceFilter: true,
        showEnergyCertificate: true
      },
      [FormEstateType.STORAGE_PRODUCTION]: {
        showSpaceFilter: true,
        showEnergyCertificate: true
      },
      [FormEstateType.AGRICULTURE_FORESTRY]: {
        showSpaceFilter: true
      },
      [FormEstateType.TRADING]: {
        showSpaceFilter: true,
        showEnergyCertificate: true
      },
      [FormEstateType.GASTRONOMY_HOTEL]: {
        showSpaceFilter: true,
        showRoomsFilter: true,
        showYearofConstructionFilter: true,
        showEnergyCertificate: true
      },
      [FormEstateType.PLOT]: {
        showPlotFilter: true
      },
      [FormEstateType.PARKING]: {},
      [FormEstateType.MISCELLANEOUS]: {
        showPlotFilter: true,
        showSpaceFilter: true,
        showEnergyCertificate: true
      },
      [FormEstateType.HOUSE_APARTMENT]: {},
      [FormEstateType.HOUSE]: {},
      [FormEstateType.APARTMENT]: {}
    },
    [FormDistributionType.RENT]: {
      [FormEstateType.OFFICE]: {
        showSpaceFilter: true,
        showEnergyCertificate: true
      },
      [FormEstateType.STORAGE_PRODUCTION]: {
        showSpaceFilter: true,
        showEnergyCertificate: true
      },
      [FormEstateType.AGRICULTURE_FORESTRY]: {
        showSpaceFilter: true
      },
      [FormEstateType.TRADING]: {
        showSpaceFilter: true,
        showEnergyCertificate: true
      },
      [FormEstateType.GASTRONOMY_HOTEL]: {
        showSpaceFilter: true,
        showRoomsFilter: true,
        showYearofConstructionFilter: true,
        showEnergyCertificate: true
      },
      [FormEstateType.PLOT]: {
        showPlotFilter: true
      },
      [FormEstateType.PARKING]: {},
      [FormEstateType.MISCELLANEOUS]: {
        showPlotFilter: true,
        showSpaceFilter: true,
        showEnergyCertificate: true
      },
      [FormEstateType.HOUSE_APARTMENT]: {},
      [FormEstateType.HOUSE]: {},
      [FormEstateType.APARTMENT]: {}
    }
  }
};
var ProjectTypeOptions = {
  [UsageType.RESIDENTIAL]: {
    [FormDistributionType.BUY]: {
      [FormEstateType.HOUSE_APARTMENT]: [
        FormProjectType.NEW_BUILD,
        FormProjectType.FORECLOSURE,
        FormProjectType.INVESTMENT,
        FormProjectType.PROJECTED,
        FormProjectType.RESALE
      ],
      [FormEstateType.HOUSE]: [
        FormProjectType.NEW_BUILD,
        FormProjectType.FORECLOSURE,
        FormProjectType.INVESTMENT,
        FormProjectType.PROJECTED,
        FormProjectType.RESALE
      ],
      [FormEstateType.APARTMENT]: [
        FormProjectType.NEW_BUILD,
        FormProjectType.FORECLOSURE,
        FormProjectType.INVESTMENT,
        FormProjectType.PROJECTED,
        FormProjectType.RESALE
      ],
      [FormEstateType.MISCELLANEOUS]: [
        FormProjectType.NEW_BUILD,
        FormProjectType.FORECLOSURE,
        FormProjectType.INVESTMENT,
        FormProjectType.PROJECTED,
        FormProjectType.RESALE
      ],
      [FormEstateType.PARKING]: [FormProjectType.FORECLOSURE, FormProjectType.INVESTMENT, FormProjectType.RESALE],
      [FormEstateType.PLOT]: [FormProjectType.FORECLOSURE, FormProjectType.INVESTMENT, FormProjectType.RESALE],
      [FormEstateType.OFFICE]: [],
      [FormEstateType.TRADING]: [],
      [FormEstateType.GASTRONOMY_HOTEL]: [],
      [FormEstateType.STORAGE_PRODUCTION]: [],
      [FormEstateType.AGRICULTURE_FORESTRY]: []
    },
    [FormDistributionType.RENT]: {
      [FormEstateType.HOUSE_APARTMENT]: [
        FormProjectType.NEW_BUILD,
        FormProjectType.SWAP_APARTMENT,
        FormProjectType.FLATSHARING,
        FormProjectType.SHORT_TIME_RENTAL,
        FormProjectType.STOCK
      ],
      [FormEstateType.HOUSE]: [
        FormProjectType.NEW_BUILD,
        FormProjectType.FLATSHARING,
        FormProjectType.SHORT_TIME_RENTAL,
        FormProjectType.STOCK
      ],
      [FormEstateType.APARTMENT]: [
        FormProjectType.NEW_BUILD,
        FormProjectType.SWAP_APARTMENT,
        FormProjectType.FLATSHARING,
        FormProjectType.SHORT_TIME_RENTAL,
        FormProjectType.STOCK
      ],
      [FormEstateType.PLOT]: [],
      [FormEstateType.PARKING]: [],
      [FormEstateType.MISCELLANEOUS]: [],
      [FormEstateType.OFFICE]: [],
      [FormEstateType.TRADING]: [],
      [FormEstateType.GASTRONOMY_HOTEL]: [],
      [FormEstateType.STORAGE_PRODUCTION]: [],
      [FormEstateType.AGRICULTURE_FORESTRY]: []
    }
  },
  [UsageType.COMMERCIAL]: {
    [FormDistributionType.BUY]: {
      [FormEstateType.OFFICE]: [FormProjectType.RESALE, FormProjectType.INVESTMENT],
      [FormEstateType.STORAGE_PRODUCTION]: [FormProjectType.RESALE, FormProjectType.INVESTMENT],
      [FormEstateType.AGRICULTURE_FORESTRY]: [FormProjectType.RESALE, FormProjectType.INVESTMENT],
      [FormEstateType.TRADING]: [FormProjectType.RESALE, FormProjectType.INVESTMENT],
      [FormEstateType.GASTRONOMY_HOTEL]: [FormProjectType.RESALE, FormProjectType.INVESTMENT],
      [FormEstateType.PLOT]: [FormProjectType.RESALE, FormProjectType.INVESTMENT],
      [FormEstateType.PARKING]: [FormProjectType.RESALE, FormProjectType.INVESTMENT],
      [FormEstateType.MISCELLANEOUS]: [FormProjectType.RESALE, FormProjectType.INVESTMENT],
      [FormEstateType.HOUSE_APARTMENT]: [],
      [FormEstateType.HOUSE]: [],
      [FormEstateType.APARTMENT]: []
    },
    [FormDistributionType.RENT]: {
      [FormEstateType.OFFICE]: [],
      [FormEstateType.STORAGE_PRODUCTION]: [],
      [FormEstateType.AGRICULTURE_FORESTRY]: [],
      [FormEstateType.TRADING]: [],
      [FormEstateType.GASTRONOMY_HOTEL]: [],
      [FormEstateType.PLOT]: [],
      [FormEstateType.PARKING]: [],
      [FormEstateType.MISCELLANEOUS]: [],
      [FormEstateType.HOUSE_APARTMENT]: [],
      [FormEstateType.HOUSE]: [],
      [FormEstateType.APARTMENT]: []
    }
  }
};
var defaultFiltersConditions = {
  showRoomsFilter: false,
  showBedroomsFilter: false,
  showSpaceFilter: false,
  showPlotFilter: false,
  showFloorsFilter: false,
  showYearofConstructionFilter: false,
  showSubsidizingHousing: false,
  showProviderTypeFilter: false,
  showEnergyCertificate: false,
  showListingsDisplay: false,
  showUsageTypeTabs: true,
  showEnergyTypeFilter: false,
  showDistributionTypeButtonGroupFilter: true,
  showCommuteFilter: true,
  usageTypes: [],
  estateTypes: [],
  estateSubTypes: [],
  distributionTypes: [],
  features: [],
  floors: [FloorsType.ROOF_STOREY, FloorsType.GROUND_FLOOR, FloorsType.OTHER],
  providerType: [ProviderType.PRIVATE, ProviderType.PROFESSIONAL],
  projectTypes: [],
  listingsDisplay: [],
  energyTypes: []
};

// src/brands/classifieds/immonet/index.ts
var brandConfig = {
  brand: "immonet",
  country: "de",
  defaultLocale: "de",
  defaultLocation: {
    id: "AD08DE7633",
    // Hamburg
    coordinates: {
      lat: 53.54746392032525,
      lng: 9.989213629036385
    }
  },
  variant: "nonFlagship",
  templates: {
    filters: {
      rooms: ExtendedRoomOptions,
      bedrooms: [],
      energyCertificateClasses: EnergyCertificateClasses,
      estateTypesMinimal: [
        FormEstateType.HOUSE,
        FormEstateType.APARTMENT,
        { translationKey: "filters.estate_type.checkbox.new_builds", url: "/neubauimmobilien.html" },
        { translationKey: "filters.estate_type.checkbox.commercial", url: "/gewerbe.html" }
      ],
      locationTypes: [
        PlaceType.BOROUGHS,
        PlaceType.NEIGHBORHOOD,
        PlaceType.MUNICIPALITY,
        PlaceType.PROVINCE,
        PlaceType.REGION
      ],
      energyTypes: [FormEnergyType.HEATPUMP, FormEnergyType.SOLAR, FormEnergyType.COLLECTIVE],
      // see: https://avivgroup.atlassian.net/wiki/spaces/ATSS/pages/396886022/SERP+-+Filters
      ruleSet: ruleSet(defaultFiltersConditions, EstateSubTypeOptions, {
        [UsageType.RESIDENTIAL]: {
          [FormDistributionType.BUY]: {
            [FormEstateType.HOUSE_APARTMENT]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE_APARTMENT],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE_APARTMENT],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE_APARTMENT]
            },
            [FormEstateType.HOUSE]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE]
            },
            [FormEstateType.APARTMENT]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.APARTMENT],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.APARTMENT],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.APARTMENT]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PLOT],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PLOT],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PLOT]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PARKING],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PARKING],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PARKING]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS]
            }
          },
          [FormDistributionType.RENT]: {
            [FormEstateType.HOUSE_APARTMENT]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE_APARTMENT],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE_APARTMENT],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE_APARTMENT]
            },
            [FormEstateType.HOUSE]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE]
            },
            [FormEstateType.APARTMENT]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.APARTMENT],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.APARTMENT],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.APARTMENT]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.PLOT]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.PARKING]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.MISCELLANEOUS],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.MISCELLANEOUS]
            }
          }
        },
        [UsageType.COMMERCIAL]: {
          [FormDistributionType.BUY]: {
            [FormEstateType.OFFICE]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.OFFICE],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.OFFICE],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.OFFICE]
            },
            [FormEstateType.STORAGE_PRODUCTION]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.STORAGE_PRODUCTION],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.STORAGE_PRODUCTION],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.STORAGE_PRODUCTION]
            },
            [FormEstateType.AGRICULTURE_FORESTRY]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.AGRICULTURE_FORESTRY],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.AGRICULTURE_FORESTRY],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.AGRICULTURE_FORESTRY]
            },
            [FormEstateType.TRADING]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.TRADING],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.TRADING],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.TRADING]
            },
            [FormEstateType.GASTRONOMY_HOTEL]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.GASTRONOMY_HOTEL],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.GASTRONOMY_HOTEL],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.GASTRONOMY_HOTEL]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PLOT],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PLOT],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PLOT]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PARKING],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PARKING],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PARKING]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS]
            }
          },
          [FormDistributionType.RENT]: {
            [FormEstateType.OFFICE]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.OFFICE],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.OFFICE]
            },
            [FormEstateType.STORAGE_PRODUCTION]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.STORAGE_PRODUCTION],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.STORAGE_PRODUCTION]
            },
            [FormEstateType.AGRICULTURE_FORESTRY]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.AGRICULTURE_FORESTRY],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.AGRICULTURE_FORESTRY]
            },
            [FormEstateType.TRADING]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.TRADING],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.TRADING]
            },
            [FormEstateType.GASTRONOMY_HOTEL]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.GASTRONOMY_HOTEL],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.GASTRONOMY_HOTEL]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.PLOT],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.PLOT]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.PARKING],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.PARKING]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.MISCELLANEOUS],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.MISCELLANEOUS]
            }
          }
        }
      })
    }
  },
  transformers: {
    useFor: {
      [FormEstateType.PLOT]: {
        [UsageType.COMMERCIAL]: [UseFor.MIXED, UseFor.COMMERCIAL, UseFor.OFFICE, UseFor.INDUSTRIAL],
        [UsageType.RESIDENTIAL]: [UseFor.MIXED, UseFor.LIVING]
      },
      [FormEstateType.PARKING]: {
        [UsageType.COMMERCIAL]: [UseFor.MIXED, UseFor.COMMERCIAL, UseFor.OFFICE, UseFor.INDUSTRIAL],
        [UsageType.RESIDENTIAL]: [UseFor.MIXED, UseFor.LIVING]
      },
      [FormEstateType.MISCELLANEOUS]: {
        [UsageType.COMMERCIAL]: [UseFor.MIXED, UseFor.COMMERCIAL, UseFor.OFFICE, UseFor.INDUSTRIAL],
        [UsageType.RESIDENTIAL]: [UseFor.MIXED, UseFor.LIVING]
      }
    }
  }
};

// src/brands/classifieds/fr/energyCertificateOptions.ts
var EnergyCertificateClasses2 = [
  { value: "A", barLabel: "< 71", size: 0, color: "color.scales.energy.green100" },
  { value: "B", barLabel: "71 - 110", size: 71, color: "color.scales.energy.green200" },
  { value: "C", barLabel: "111 - 180", size: 111, color: "color.scales.energy.green400" },
  { value: "D", barLabel: "181 - 250", size: 181, color: "color.scales.energy.yellow100" },
  { value: "E", barLabel: "251 - 330", size: 251, color: "color.scales.energy.orange200" },
  { value: "F", barLabel: "331 - 420", size: 331, color: "color.scales.energy.red100" },
  { value: "G", barLabel: "> 420", size: 421, color: "color.scales.energy.red200" }
];
var EstateSubTypeOptions2 = {
  [FormEstateType.HOUSE_APARTMENT]: [],
  [FormEstateType.HOUSE]: [
    FormEstateSubType.CASTLE,
    FormEstateSubType.CHALET,
    FormEstateSubType.FARMHOUSE,
    FormEstateSubType.MANOR_HOUSE,
    FormEstateSubType.SEMIDETACHED_HOUSE,
    FormEstateSubType.TOWN_HOUSE,
    FormEstateSubType.VILLA,
    FormEstateSubType.APARTMENT_HOUSE,
    FormEstateSubType.MANSION
  ],
  [FormEstateType.APARTMENT]: [FormEstateSubType.LOFT, FormEstateSubType.MULTI_STOREY, FormEstateSubType.STUDIO],
  [FormEstateType.PARKING]: [],
  [FormEstateType.PLOT]: [FormEstateSubType.LEISURE_FACILITY],
  [FormEstateType.AGRICULTURE_FORESTRY]: [
    FormEstateSubType.AGRICULTURE_COMPANY,
    FormEstateSubType.FARM_RANCH,
    FormEstateSubType.HUNTING_FISHING,
    FormEstateSubType.RIDING,
    FormEstateSubType.WINERY
  ],
  [FormEstateType.GASTRONOMY_HOTEL]: [
    FormEstateSubType.CAFE_BAR_PUB,
    FormEstateSubType.CLUB_DISCOTHEQUE,
    FormEstateSubType.HOTEL_PENSION,
    FormEstateSubType.RESTAURANT
  ],
  [FormEstateType.OFFICE]: [FormEstateSubType.LIVING_AND_COMMERCIAL_BUILDING, FormEstateSubType.OFFICE],
  [FormEstateType.STORAGE_PRODUCTION]: [
    FormEstateSubType.GARAGE_REPAIR,
    FormEstateSubType.PRODUCTION_HALL,
    FormEstateSubType.WAREHOUSE_HALL
  ],
  [FormEstateType.TRADING]: [FormEstateSubType.SHOPPING_CENTRE, FormEstateSubType.STORE],
  [FormEstateType.MISCELLANEOUS]: []
};

// src/brands/classifieds/immoweb/index.ts
var brandConfig2 = {
  brand: "immoweb",
  country: "be",
  defaultLocale: "fr",
  variant: "flagship",
  defaultLocation: {
    id: "AD08BE73",
    // Brussels
    coordinates: {
      lat: 50.873,
      lng: 4.375
    }
  },
  templates: {
    filters: {
      rooms: StandardRoomOptions,
      bedrooms: StandardBedroomOptions,
      energyCertificateClasses: EnergyCertificateClasses2,
      energyTypes: [],
      estateTypesMinimal: [FormEstateType.HOUSE, FormEstateType.APARTMENT],
      locationTypes: [
        PlaceType.PROVINCE,
        PlaceType.COUNTY,
        PlaceType.MUNICIPALITY,
        PlaceType.MACRO_NEIGHBORHOOD,
        PlaceType.POSTCODE
      ],
      ruleSet: ruleSet(defaultFiltersConditions, EstateSubTypeOptions2, {})
    }
  }
};
var brandConfig3 = {
  brand: "immowelt",
  country: "de",
  defaultLocale: "de",
  variant: "flagship",
  defaultLocation: {
    id: "AD08DE7633",
    // Hamburg
    coordinates: {
      lat: 53.54746392032525,
      lng: 9.989213629036385
    }
  },
  templates: {
    filters: {
      rooms: ExtendedRoomOptions,
      bedrooms: [],
      energyCertificateClasses: EnergyCertificateClasses,
      estateTypesMinimal: [
        FormEstateType.HOUSE,
        FormEstateType.APARTMENT,
        { translationKey: "filters.estate_type.checkbox.new_builds", url: "/neubauimmobilien.html" },
        { translationKey: "filters.estate_type.checkbox.commercial", url: "/gewerbe.html" }
      ],
      locationTypes: [
        PlaceType.BOROUGHS,
        PlaceType.NEIGHBORHOOD,
        PlaceType.MUNICIPALITY,
        PlaceType.PROVINCE,
        PlaceType.REGION
      ],
      energyTypes: [FormEnergyType.HEATPUMP, FormEnergyType.SOLAR, FormEnergyType.COLLECTIVE],
      // see: https://avivgroup.atlassian.net/wiki/spaces/ATSS/pages/396886022/SERP+-+Filters
      ruleSet: ruleSet(defaultFiltersConditions, EstateSubTypeOptions, {
        [UsageType.RESIDENTIAL]: {
          [FormDistributionType.BUY]: {
            [FormEstateType.HOUSE_APARTMENT]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE_APARTMENT],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE_APARTMENT],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE_APARTMENT]
            },
            [FormEstateType.HOUSE]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE]
            },
            [FormEstateType.APARTMENT]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.APARTMENT],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.APARTMENT],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.APARTMENT]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PLOT],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PLOT],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PLOT]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PARKING],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PARKING],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PARKING]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS]
            }
          },
          [FormDistributionType.RENT]: {
            [FormEstateType.HOUSE_APARTMENT]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE_APARTMENT],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE_APARTMENT],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE_APARTMENT]
            },
            [FormEstateType.HOUSE]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE]
            },
            [FormEstateType.APARTMENT]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.APARTMENT],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.APARTMENT],
              projectTypes: ProjectTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.APARTMENT]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.PLOT]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.PARKING]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.MISCELLANEOUS],
              features: FeatureOptions[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.MISCELLANEOUS]
            }
          }
        },
        [UsageType.COMMERCIAL]: {
          [FormDistributionType.BUY]: {
            [FormEstateType.OFFICE]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.OFFICE],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.OFFICE],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.OFFICE]
            },
            [FormEstateType.STORAGE_PRODUCTION]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.STORAGE_PRODUCTION],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.STORAGE_PRODUCTION],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.STORAGE_PRODUCTION]
            },
            [FormEstateType.AGRICULTURE_FORESTRY]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.AGRICULTURE_FORESTRY],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.AGRICULTURE_FORESTRY],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.AGRICULTURE_FORESTRY]
            },
            [FormEstateType.TRADING]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.TRADING],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.TRADING],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.TRADING]
            },
            [FormEstateType.GASTRONOMY_HOTEL]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.GASTRONOMY_HOTEL],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.GASTRONOMY_HOTEL],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.GASTRONOMY_HOTEL]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PLOT],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PLOT],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PLOT]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PARKING],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PARKING],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PARKING]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS],
              projectTypes: ProjectTypeOptions[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS]
            }
          },
          [FormDistributionType.RENT]: {
            [FormEstateType.OFFICE]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.OFFICE],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.OFFICE]
            },
            [FormEstateType.STORAGE_PRODUCTION]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.STORAGE_PRODUCTION],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.STORAGE_PRODUCTION]
            },
            [FormEstateType.AGRICULTURE_FORESTRY]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.AGRICULTURE_FORESTRY],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.AGRICULTURE_FORESTRY]
            },
            [FormEstateType.TRADING]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.TRADING],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.TRADING]
            },
            [FormEstateType.GASTRONOMY_HOTEL]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.GASTRONOMY_HOTEL],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.GASTRONOMY_HOTEL]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.PLOT],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.PLOT]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.PARKING],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.PARKING]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.MISCELLANEOUS],
              features: FeatureOptions[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.MISCELLANEOUS]
            }
          }
        }
      })
    }
  },
  transformers: {
    useFor: {
      [FormEstateType.PLOT]: {
        [UsageType.COMMERCIAL]: [UseFor.MIXED, UseFor.COMMERCIAL, UseFor.OFFICE, UseFor.INDUSTRIAL],
        [UsageType.RESIDENTIAL]: [UseFor.MIXED, UseFor.LIVING]
      },
      [FormEstateType.PARKING]: {
        [UsageType.COMMERCIAL]: [UseFor.MIXED, UseFor.COMMERCIAL, UseFor.OFFICE, UseFor.INDUSTRIAL],
        [UsageType.RESIDENTIAL]: [UseFor.MIXED, UseFor.LIVING]
      },
      [FormEstateType.MISCELLANEOUS]: {
        [UsageType.COMMERCIAL]: [UseFor.MIXED, UseFor.COMMERCIAL, UseFor.OFFICE, UseFor.INDUSTRIAL],
        [UsageType.RESIDENTIAL]: [UseFor.MIXED, UseFor.LIVING]
      }
    }
  }
};
var FeatureOptions2 = {
  [UsageType.RESIDENTIAL]: {
    [DistributionType.BUY]: {
      [FormEstateType.HOUSE_APARTMENT]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.GARDEN,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.SWIMMING_POOL,
        FormFeatureType.KITCHEN_FULLY_EQUIPPED,
        FormFeatureType.CELLAR,
        FormFeatureType.VACANT,
        FormFeatureType.FURNISHED,
        FormFeatureType.UNFURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.NEED_OF_RENOVATION,
        FormFeatureType.UNDERFLOOR
      ],
      [FormEstateType.HOUSE]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.GARDEN,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.SWIMMING_POOL,
        FormFeatureType.KITCHEN_FULLY_EQUIPPED,
        FormFeatureType.CELLAR,
        FormFeatureType.VACANT,
        FormFeatureType.FURNISHED,
        FormFeatureType.UNFURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.NEED_OF_RENOVATION,
        FormFeatureType.UNDERFLOOR
      ],
      [FormEstateType.APARTMENT]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.GARDEN,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.SWIMMING_POOL,
        FormFeatureType.KITCHEN_FULLY_EQUIPPED,
        FormFeatureType.CELLAR,
        FormFeatureType.NEED_OF_RENOVATION,
        FormFeatureType.VACANT,
        FormFeatureType.FURNISHED,
        FormFeatureType.UNFURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.UNDERFLOOR
      ],
      [FormEstateType.MISCELLANEOUS]: [FormFeatureType.NEED_OF_RENOVATION],
      [FormEstateType.PARKING]: [],
      [FormEstateType.PLOT]: [],
      [FormEstateType.OFFICE]: [],
      [FormEstateType.TRADING]: [],
      [FormEstateType.GASTRONOMY_HOTEL]: [],
      [FormEstateType.STORAGE_PRODUCTION]: [],
      [FormEstateType.AGRICULTURE_FORESTRY]: []
    },
    [DistributionType.RENT]: {
      [FormEstateType.HOUSE_APARTMENT]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.GARDEN,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.SWIMMING_POOL,
        FormFeatureType.KITCHEN_FULLY_EQUIPPED,
        FormFeatureType.CELLAR,
        FormFeatureType.VACANT,
        FormFeatureType.FURNISHED,
        FormFeatureType.UNFURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.UNDERFLOOR
      ],
      [FormEstateType.HOUSE]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.GARDEN,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.SWIMMING_POOL,
        FormFeatureType.KITCHEN_FULLY_EQUIPPED,
        FormFeatureType.CELLAR,
        FormFeatureType.VACANT,
        FormFeatureType.FURNISHED,
        FormFeatureType.UNFURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.UNDERFLOOR
      ],
      [FormEstateType.APARTMENT]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.GARDEN,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.SWIMMING_POOL,
        FormFeatureType.KITCHEN_FULLY_EQUIPPED,
        FormFeatureType.CELLAR,
        FormFeatureType.VACANT,
        FormFeatureType.FURNISHED,
        FormFeatureType.UNFURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.UNDERFLOOR
      ],
      [FormEstateType.PLOT]: [],
      [FormEstateType.PARKING]: [],
      [FormEstateType.MISCELLANEOUS]: [FormFeatureType.VACANT],
      [FormEstateType.OFFICE]: [],
      [FormEstateType.TRADING]: [],
      [FormEstateType.GASTRONOMY_HOTEL]: [],
      [FormEstateType.STORAGE_PRODUCTION]: [],
      [FormEstateType.AGRICULTURE_FORESTRY]: []
    }
  },
  [UsageType.COMMERCIAL]: {
    [DistributionType.BUY]: {
      [FormEstateType.OFFICE]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.CELLAR,
        FormFeatureType.FURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR,
        FormFeatureType.NEED_OF_RENOVATION
      ],
      [FormEstateType.STORAGE_PRODUCTION]: [FormFeatureType.PARKING_GARAGE, FormFeatureType.REDUCE_MOBILITY_ACCESS],
      [FormEstateType.AGRICULTURE_FORESTRY]: [FormFeatureType.PARKING_GARAGE, FormFeatureType.REDUCE_MOBILITY_ACCESS],
      [FormEstateType.TRADING]: [
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.CELLAR,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR
      ],
      [FormEstateType.GASTRONOMY_HOTEL]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.SWIMMING_POOL,
        FormFeatureType.CELLAR,
        FormFeatureType.FURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR
      ],
      [FormEstateType.PLOT]: [],
      [FormEstateType.PARKING]: [FormFeatureType.REDUCE_MOBILITY_ACCESS],
      [FormEstateType.MISCELLANEOUS]: [],
      [FormEstateType.HOUSE_APARTMENT]: [],
      [FormEstateType.HOUSE]: [],
      [FormEstateType.APARTMENT]: []
    },
    [DistributionType.RENT]: {
      [FormEstateType.OFFICE]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.CELLAR,
        FormFeatureType.FURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR
      ],
      [FormEstateType.STORAGE_PRODUCTION]: [FormFeatureType.PARKING_GARAGE, FormFeatureType.REDUCE_MOBILITY_ACCESS],
      [FormEstateType.AGRICULTURE_FORESTRY]: [FormFeatureType.PARKING_GARAGE, FormFeatureType.REDUCE_MOBILITY_ACCESS],
      [FormEstateType.TRADING]: [
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.CELLAR,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR
      ],
      [FormEstateType.GASTRONOMY_HOTEL]: [
        FormFeatureType.BALCONY_TERRACE,
        FormFeatureType.PARKING_GARAGE,
        FormFeatureType.SWIMMING_POOL,
        FormFeatureType.CELLAR,
        FormFeatureType.FURNISHED,
        FormFeatureType.REDUCE_MOBILITY_ACCESS,
        FormFeatureType.ELEVATOR
      ],
      [FormEstateType.PLOT]: [],
      [FormEstateType.PARKING]: [FormFeatureType.REDUCE_MOBILITY_ACCESS],
      [FormEstateType.MISCELLANEOUS]: [],
      [FormEstateType.HOUSE_APARTMENT]: [],
      [FormEstateType.HOUSE]: [],
      [FormEstateType.APARTMENT]: []
    }
  }
};
var FilterTypeOptions2 = {
  [UsageType.RESIDENTIAL]: {
    [FormDistributionType.BUY]: {
      [FormEstateType.HOUSE_APARTMENT]: {
        showRoomsFilter: true,
        showBedroomsFilter: true,
        showSpaceFilter: true,
        showYearofConstructionFilter: true,
        showFloorsFilter: true,
        showProviderTypeFilter: false,
        showEnergyCertificate: true,
        showEnergyTypeFilter: true,
        showPlotFilter: true,
        showListingsDisplay: true
      },
      [FormEstateType.HOUSE]: {
        showRoomsFilter: true,
        showBedroomsFilter: true,
        showSpaceFilter: true,
        showPlotFilter: true,
        showYearofConstructionFilter: true,
        showProviderTypeFilter: false,
        showEnergyCertificate: true,
        showEnergyTypeFilter: true,
        showListingsDisplay: true
      },
      [FormEstateType.APARTMENT]: {
        showRoomsFilter: true,
        showBedroomsFilter: true,
        showSpaceFilter: true,
        showYearofConstructionFilter: true,
        showFloorsFilter: true,
        showProviderTypeFilter: false,
        showEnergyCertificate: true,
        showEnergyTypeFilter: true,
        showListingsDisplay: true
      },
      [FormEstateType.PLOT]: {
        showPlotFilter: true,
        showProviderTypeFilter: false,
        showListingsDisplay: true
      },
      [FormEstateType.PARKING]: {
        showProviderTypeFilter: false,
        showListingsDisplay: true
      },
      [FormEstateType.MISCELLANEOUS]: {
        showRoomsFilter: true,
        showPlotFilter: true,
        showSpaceFilter: true,
        showYearofConstructionFilter: true,
        showProviderTypeFilter: false,
        showEnergyCertificate: true,
        showListingsDisplay: true
      },
      [FormEstateType.OFFICE]: {},
      [FormEstateType.TRADING]: {},
      [FormEstateType.GASTRONOMY_HOTEL]: {},
      [FormEstateType.STORAGE_PRODUCTION]: {},
      [FormEstateType.AGRICULTURE_FORESTRY]: {}
    },
    [FormDistributionType.RENT]: {
      [FormEstateType.HOUSE_APARTMENT]: {
        showRoomsFilter: true,
        showBedroomsFilter: true,
        showSpaceFilter: true,
        showYearofConstructionFilter: true,
        showFloorsFilter: true,
        showProviderTypeFilter: false,
        showEnergyCertificate: true,
        showEnergyTypeFilter: true,
        showSubsidizingHousing: false,
        showPlotFilter: true
      },
      [FormEstateType.HOUSE]: {
        showRoomsFilter: true,
        showBedroomsFilter: true,
        showSpaceFilter: true,
        showPlotFilter: true,
        showYearofConstructionFilter: true,
        showProviderTypeFilter: false,
        showEnergyCertificate: true,
        showEnergyTypeFilter: true,
        showSubsidizingHousing: false
      },
      [FormEstateType.APARTMENT]: {
        showRoomsFilter: true,
        showBedroomsFilter: true,
        showSpaceFilter: true,
        showYearofConstructionFilter: true,
        showFloorsFilter: true,
        showProviderTypeFilter: false,
        showEnergyCertificate: true,
        showEnergyTypeFilter: true,
        showSubsidizingHousing: false
      },
      [FormEstateType.PLOT]: {
        showPlotFilter: true,
        showProviderTypeFilter: false
      },
      [FormEstateType.PARKING]: {
        showProviderTypeFilter: true
      },
      [FormEstateType.MISCELLANEOUS]: {
        showRoomsFilter: true,
        showPlotFilter: true,
        showSpaceFilter: true,
        showYearofConstructionFilter: true,
        showProviderTypeFilter: false,
        showEnergyCertificate: true
      },
      [FormEstateType.OFFICE]: {},
      [FormEstateType.TRADING]: {},
      [FormEstateType.GASTRONOMY_HOTEL]: {},
      [FormEstateType.STORAGE_PRODUCTION]: {},
      [FormEstateType.AGRICULTURE_FORESTRY]: {}
    }
  },
  [UsageType.COMMERCIAL]: {
    [FormDistributionType.BUY]: {
      [FormEstateType.OFFICE]: {
        showSpaceFilter: true
      },
      [FormEstateType.STORAGE_PRODUCTION]: {
        showSpaceFilter: true
      },
      [FormEstateType.AGRICULTURE_FORESTRY]: {
        showSpaceFilter: true
      },
      [FormEstateType.TRADING]: {
        showSpaceFilter: true
      },
      [FormEstateType.GASTRONOMY_HOTEL]: {
        showSpaceFilter: true
      },
      [FormEstateType.PLOT]: {
        showPlotFilter: true,
        showEnergyCertificate: false
      },
      [FormEstateType.PARKING]: {},
      [FormEstateType.MISCELLANEOUS]: {},
      [FormEstateType.HOUSE_APARTMENT]: {},
      [FormEstateType.HOUSE]: {},
      [FormEstateType.APARTMENT]: {}
    },
    [FormDistributionType.RENT]: {
      [FormEstateType.OFFICE]: {
        showSpaceFilter: true
      },
      [FormEstateType.STORAGE_PRODUCTION]: {
        showSpaceFilter: true
      },
      [FormEstateType.AGRICULTURE_FORESTRY]: {
        showSpaceFilter: true
      },
      [FormEstateType.TRADING]: {
        showSpaceFilter: true
      },
      [FormEstateType.PLOT]: {
        showPlotFilter: true
      },
      [FormEstateType.PARKING]: {},
      [FormEstateType.MISCELLANEOUS]: {},
      [FormEstateType.GASTRONOMY_HOTEL]: {},
      [FormEstateType.HOUSE_APARTMENT]: {},
      [FormEstateType.HOUSE]: {},
      [FormEstateType.APARTMENT]: {}
    }
  }
};

// src/brands/classifieds/logicimmo/index.ts
var brandConfig4 = {
  brand: "logicimmo",
  country: "fr",
  defaultLocale: "fr",
  defaultLocation: {
    id: "AD06FR76",
    // Paris
    coordinates: {
      lat: 48.85658483336071,
      lng: 2.3429881280649387
    }
  },
  variant: "nonFlagship",
  templates: {
    filters: {
      rooms: StandardRoomOptions,
      bedrooms: StandardBedroomOptions,
      energyCertificateClasses: EnergyCertificateClasses2,
      energyTypes: [FormEnergyType.COLLECTIVE],
      estateTypesMinimal: [FormEstateType.HOUSE, FormEstateType.APARTMENT],
      locationTypes: [
        PlaceType.MACRO_NEIGHBORHOOD,
        PlaceType.MICRO_NEIGHBORHOOD,
        PlaceType.BOROUGHS,
        PlaceType.NEIGHBORHOOD,
        PlaceType.MUNICIPALITY,
        PlaceType.PROVINCE,
        PlaceType.REGION
      ],
      // see: https://avivgroup.atlassian.net/wiki/spaces/ATSS/pages/396886022/SERP+-+Filters
      ruleSet: ruleSet(defaultFiltersConditions, EstateSubTypeOptions2, {
        [UsageType.RESIDENTIAL]: {
          [FormDistributionType.BUY]: {
            [FormEstateType.HOUSE_APARTMENT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE_APARTMENT],
              features: FeatureOptions2[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE_APARTMENT],
              projectTypes: [
                FormProjectType.NEW_BUILD,
                FormProjectType.PROJECTED,
                FormProjectType.RESALE,
                FormProjectType.LIFE_ANNUITY
              ],
              listingsDisplay: [ListingsDisplayType.EXCLUSIVE_MANDATE]
            },
            [FormEstateType.HOUSE]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE],
              features: FeatureOptions2[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.HOUSE],
              projectTypes: [
                FormProjectType.NEW_BUILD,
                FormProjectType.PROJECTED,
                FormProjectType.RESALE,
                FormProjectType.LIFE_ANNUITY
              ],
              listingsDisplay: [ListingsDisplayType.EXCLUSIVE_MANDATE]
            },
            [FormEstateType.APARTMENT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.APARTMENT],
              features: FeatureOptions2[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.APARTMENT],
              projectTypes: [FormProjectType.NEW_BUILD, FormProjectType.RESALE, FormProjectType.LIFE_ANNUITY],
              listingsDisplay: [ListingsDisplayType.EXCLUSIVE_MANDATE]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PLOT],
              features: FeatureOptions2[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PLOT],
              projectTypes: [],
              listingsDisplay: [ListingsDisplayType.EXCLUSIVE_MANDATE]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PARKING],
              features: FeatureOptions2[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.PARKING],
              projectTypes: [],
              listingsDisplay: [ListingsDisplayType.EXCLUSIVE_MANDATE]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS],
              features: FeatureOptions2[UsageType.RESIDENTIAL][FormDistributionType.BUY][FormEstateType.MISCELLANEOUS],
              projectTypes: [FormProjectType.NEW_BUILD, FormProjectType.RESALE, FormProjectType.LIFE_ANNUITY],
              listingsDisplay: [ListingsDisplayType.EXCLUSIVE_MANDATE]
            }
          },
          [FormDistributionType.RENT]: {
            [FormEstateType.HOUSE_APARTMENT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE_APARTMENT],
              features: FeatureOptions2[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE_APARTMENT],
              projectTypes: []
            },
            [FormEstateType.HOUSE]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE],
              features: FeatureOptions2[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.HOUSE],
              projectTypes: []
            },
            [FormEstateType.APARTMENT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.APARTMENT],
              features: FeatureOptions2[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.APARTMENT],
              projectTypes: []
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.PLOT]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.PARKING]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.MISCELLANEOUS],
              features: FeatureOptions2[UsageType.RESIDENTIAL][FormDistributionType.RENT][FormEstateType.MISCELLANEOUS]
            }
          }
        },
        [UsageType.COMMERCIAL]: {
          [FormDistributionType.BUY]: {
            [FormEstateType.OFFICE]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.OFFICE],
              estateSubTypes: []
            },
            [FormEstateType.STORAGE_PRODUCTION]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.STORAGE_PRODUCTION]
            },
            [FormEstateType.AGRICULTURE_FORESTRY]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.AGRICULTURE_FORESTRY]
            },
            [FormEstateType.TRADING]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.TRADING],
              estateSubTypes: [FormEstateSubType.STORE, FormEstateSubType.BOUTIQUE]
            },
            [FormEstateType.GASTRONOMY_HOTEL]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.GASTRONOMY_HOTEL],
              estateSubTypes: []
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][FormDistributionType.BUY][FormEstateType.PLOT],
              estateSubTypes: []
            }
          },
          [FormDistributionType.RENT]: {
            [FormEstateType.OFFICE]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.OFFICE],
              estateSubTypes: []
            },
            [FormEstateType.STORAGE_PRODUCTION]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.STORAGE_PRODUCTION]
            },
            [FormEstateType.AGRICULTURE_FORESTRY]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.AGRICULTURE_FORESTRY]
            },
            [FormEstateType.TRADING]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.TRADING],
              estateSubTypes: [FormEstateSubType.STORE, FormEstateSubType.BOUTIQUE]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][FormDistributionType.RENT][FormEstateType.PLOT],
              estateSubTypes: []
            }
          }
        }
      })
    }
  },
  transformers: {
    useFor: {
      [FormEstateType.PLOT]: {
        [UsageType.COMMERCIAL]: [UseFor.COMMERCIAL, UseFor.OFFICE, UseFor.INDUSTRIAL],
        [UsageType.RESIDENTIAL]: [UseFor.MIXED, UseFor.LIVING]
      }
    }
  }
};
var brandConfig5 = {
  brand: "seloger",
  country: "fr",
  defaultLocale: "fr",
  variant: "flagship",
  defaultLocation: {
    id: "AD06FR76",
    // Paris
    coordinates: {
      lat: 48.85658483336071,
      lng: 2.3429881280649387
    }
  },
  templates: {
    filters: {
      rooms: StandardRoomOptions,
      bedrooms: StandardBedroomOptions,
      energyCertificateClasses: EnergyCertificateClasses2,
      energyTypes: [FormEnergyType.COLLECTIVE],
      estateTypesMinimal: [FormEstateType.HOUSE, FormEstateType.APARTMENT],
      locationTypes: [
        PlaceType.MACRO_NEIGHBORHOOD,
        PlaceType.MICRO_NEIGHBORHOOD,
        PlaceType.BOROUGHS,
        PlaceType.NEIGHBORHOOD,
        PlaceType.MUNICIPALITY,
        PlaceType.PROVINCE,
        PlaceType.REGION
      ],
      // see: https://avivgroup.atlassian.net/wiki/spaces/ATSS/pages/396886022/SERP+-+Filters
      ruleSet: ruleSet(defaultFiltersConditions, EstateSubTypeOptions2, {
        [UsageType.RESIDENTIAL]: {
          [DistributionType.BUY]: {
            [FormEstateType.HOUSE_APARTMENT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.HOUSE_APARTMENT],
              showProviderTypeFilter: true,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.HOUSE_APARTMENT],
              projectTypes: [
                FormProjectType.NEW_BUILD,
                FormProjectType.PROJECTED,
                FormProjectType.RESALE,
                FormProjectType.LIFE_ANNUITY
              ],
              listingsDisplay: [ListingsDisplayType.EXCLUSIVE_MANDATE]
            },
            [FormEstateType.HOUSE]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.HOUSE],
              showProviderTypeFilter: true,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.HOUSE],
              projectTypes: [
                FormProjectType.NEW_BUILD,
                FormProjectType.PROJECTED,
                FormProjectType.RESALE,
                FormProjectType.LIFE_ANNUITY
              ],
              listingsDisplay: [ListingsDisplayType.EXCLUSIVE_MANDATE]
            },
            [FormEstateType.APARTMENT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.APARTMENT],
              showProviderTypeFilter: true,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.APARTMENT],
              projectTypes: [FormProjectType.NEW_BUILD, FormProjectType.RESALE, FormProjectType.LIFE_ANNUITY],
              listingsDisplay: [ListingsDisplayType.EXCLUSIVE_MANDATE]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.PLOT],
              showProviderTypeFilter: true,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.PLOT],
              projectTypes: [],
              listingsDisplay: [ListingsDisplayType.EXCLUSIVE_MANDATE]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.PARKING],
              showProviderTypeFilter: true,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.PARKING],
              projectTypes: [],
              listingsDisplay: [ListingsDisplayType.EXCLUSIVE_MANDATE]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.MISCELLANEOUS],
              showProviderTypeFilter: true,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.MISCELLANEOUS],
              projectTypes: [FormProjectType.NEW_BUILD, FormProjectType.RESALE, FormProjectType.LIFE_ANNUITY],
              listingsDisplay: [ListingsDisplayType.EXCLUSIVE_MANDATE]
            }
          },
          [DistributionType.RENT]: {
            [FormEstateType.HOUSE_APARTMENT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.RENT][FormEstateType.HOUSE_APARTMENT],
              showProviderTypeFilter: true,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.RENT][FormEstateType.HOUSE_APARTMENT],
              projectTypes: []
            },
            [FormEstateType.HOUSE]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.RENT][FormEstateType.HOUSE],
              showProviderTypeFilter: true,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.RENT][FormEstateType.HOUSE],
              projectTypes: []
            },
            [FormEstateType.APARTMENT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.RENT][FormEstateType.APARTMENT],
              showProviderTypeFilter: true,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.RENT][FormEstateType.APARTMENT],
              projectTypes: []
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.RENT][FormEstateType.PLOT],
              showProviderTypeFilter: true
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.RENT][FormEstateType.PARKING]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.RENT][FormEstateType.MISCELLANEOUS],
              showProviderTypeFilter: true,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.RENT][FormEstateType.MISCELLANEOUS]
            }
          }
        },
        [UsageType.COMMERCIAL]: {
          [DistributionType.BUY]: {
            [FormEstateType.OFFICE]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.OFFICE],
              estateSubTypes: []
            },
            [FormEstateType.STORAGE_PRODUCTION]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.STORAGE_PRODUCTION]
            },
            [FormEstateType.AGRICULTURE_FORESTRY]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.AGRICULTURE_FORESTRY]
            },
            [FormEstateType.TRADING]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.TRADING],
              estateSubTypes: [FormEstateSubType.STORE, FormEstateSubType.BOUTIQUE]
            },
            [FormEstateType.GASTRONOMY_HOTEL]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.GASTRONOMY_HOTEL],
              estateSubTypes: []
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.PLOT],
              estateSubTypes: []
            }
          },
          [DistributionType.RENT]: {
            [FormEstateType.OFFICE]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.RENT][FormEstateType.OFFICE],
              estateSubTypes: []
            },
            [FormEstateType.STORAGE_PRODUCTION]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.RENT][FormEstateType.STORAGE_PRODUCTION]
            },
            [FormEstateType.AGRICULTURE_FORESTRY]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.RENT][FormEstateType.AGRICULTURE_FORESTRY]
            },
            [FormEstateType.TRADING]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.RENT][FormEstateType.TRADING],
              estateSubTypes: [FormEstateSubType.STORE, FormEstateSubType.BOUTIQUE]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.RENT][FormEstateType.PLOT],
              estateSubTypes: []
            }
          }
        }
      })
    }
  },
  transformers: {
    useFor: {
      [FormEstateType.PLOT]: {
        [UsageType.COMMERCIAL]: [UseFor.COMMERCIAL, UseFor.OFFICE, UseFor.INDUSTRIAL],
        [UsageType.RESIDENTIAL]: [UseFor.MIXED, UseFor.LIVING]
      },
      [FormEstateType.PARKING]: {
        [UsageType.COMMERCIAL]: [UseFor.COMMERCIAL, UseFor.OFFICE, UseFor.INDUSTRIAL],
        [UsageType.RESIDENTIAL]: [UseFor.MIXED, UseFor.LIVING]
      },
      [FormEstateType.MISCELLANEOUS]: {
        [UsageType.COMMERCIAL]: [UseFor.COMMERCIAL, UseFor.OFFICE, UseFor.INDUSTRIAL],
        [UsageType.RESIDENTIAL]: [UseFor.MIXED, UseFor.LIVING]
      }
    }
  }
};
var defaultFiltersConditions2 = {
  showRoomsFilter: false,
  showBedroomsFilter: false,
  showSpaceFilter: false,
  showPlotFilter: false,
  showFloorsFilter: false,
  showYearofConstructionFilter: false,
  showSubsidizingHousing: false,
  showProviderTypeFilter: false,
  showEnergyCertificate: false,
  showListingsDisplay: false,
  showUsageTypeTabs: false,
  showEnergyTypeFilter: false,
  showDistributionTypeButtonGroupFilter: false,
  showCommuteFilter: false,
  usageTypes: [],
  estateTypes: [],
  estateSubTypes: [],
  distributionTypes: [],
  features: [],
  floors: [FloorsType.ROOF_STOREY, FloorsType.GROUND_FLOOR, FloorsType.OTHER],
  providerType: [ProviderType.PRIVATE, ProviderType.PROFESSIONAL],
  projectTypes: [],
  listingsDisplay: [],
  energyTypes: []
};

// src/brands/projects/immonet/index.ts
var brandConfig6 = {
  brand: "immonet",
  country: "de",
  defaultLocale: "de",
  variant: "nonFlagship",
  defaultLocation: {
    id: "AD08DE7633",
    // Hamburg
    coordinates: {
      lat: 53.6448575784306,
      lng: 9.629363416912954
    }
  },
  templates: {
    filters: {
      rooms: ExtendedRoomOptions,
      bedrooms: [],
      energyCertificateClasses: EnergyCertificateClasses,
      energyTypes: [],
      estateTypesMinimal: [
        FormEstateType.HOUSE,
        FormEstateType.APARTMENT,
        { translationKey: "filters.estate_type.checkbox.new_builds", url: "/neubauimmobilien.html" },
        { translationKey: "filters.estate_type.checkbox.commercial", url: "/gewerbe.html" }
      ],
      locationTypes: [
        PlaceType.BOROUGHS,
        PlaceType.NEIGHBORHOOD,
        PlaceType.MUNICIPALITY,
        PlaceType.PROVINCE,
        PlaceType.REGION
      ],
      // see: https://avivgroup.atlassian.net/wiki/spaces/ATSS/pages/396886022/SERP+-+Filters
      ruleSet: ruleSet(defaultFiltersConditions2, EstateSubTypeOptions, {
        [UsageType.RESIDENTIAL]: {
          [DistributionType.BUY]: {
            [FormEstateType.HOUSE_APARTMENT]: {
              showRoomsFilter: true,
              showSpaceFilter: true,
              showYearofConstructionFilter: false,
              showFloorsFilter: true,
              showProviderTypeFilter: false,
              showEnergyCertificate: true,
              showPlotFilter: true,
              features: [
                FormFeatureType.BALCONY_TERRACE,
                FormFeatureType.GARDEN,
                FormFeatureType.PARKING_GARAGE,
                FormFeatureType.KITCHEN_FULLY_EQUIPPED,
                FormFeatureType.BATHROOM_WINDOW,
                FormFeatureType.BATHROOM_BATHTUB,
                FormFeatureType.CELLAR,
                FormFeatureType.VACANT,
                FormFeatureType.FURNISHED,
                FormFeatureType.UNFURNISHED,
                FormFeatureType.REDUCE_MOBILITY_ACCESS,
                FormFeatureType.ELEVATOR,
                FormFeatureType.COMMISSION_FREE,
                FormFeatureType.NEED_OF_RENOVATION
              ]
            },
            [FormEstateType.HOUSE]: {
              showRoomsFilter: true,
              showSpaceFilter: true,
              showPlotFilter: true,
              showYearofConstructionFilter: false,
              showProviderTypeFilter: false,
              showEnergyCertificate: true,
              features: [
                FormFeatureType.BALCONY_TERRACE,
                FormFeatureType.GARDEN,
                FormFeatureType.PARKING_GARAGE,
                FormFeatureType.KITCHEN_FULLY_EQUIPPED,
                FormFeatureType.BATHROOM_WINDOW,
                FormFeatureType.BATHROOM_BATHTUB,
                FormFeatureType.CELLAR,
                FormFeatureType.VACANT,
                FormFeatureType.FURNISHED,
                FormFeatureType.UNFURNISHED,
                FormFeatureType.REDUCE_MOBILITY_ACCESS,
                FormFeatureType.COMMISSION_FREE,
                FormFeatureType.NEED_OF_RENOVATION
              ]
            },
            [FormEstateType.APARTMENT]: {
              showRoomsFilter: true,
              showSpaceFilter: true,
              showYearofConstructionFilter: false,
              showFloorsFilter: true,
              showProviderTypeFilter: false,
              showEnergyCertificate: true,
              features: [
                FormFeatureType.BALCONY_TERRACE,
                FormFeatureType.GARDEN,
                FormFeatureType.PARKING_GARAGE,
                FormFeatureType.KITCHEN_FULLY_EQUIPPED,
                FormFeatureType.BATHROOM_WINDOW,
                FormFeatureType.BATHROOM_BATHTUB,
                FormFeatureType.CELLAR,
                FormFeatureType.VACANT,
                FormFeatureType.FURNISHED,
                FormFeatureType.UNFURNISHED,
                FormFeatureType.REDUCE_MOBILITY_ACCESS,
                FormFeatureType.ELEVATOR,
                FormFeatureType.NEED_OF_RENOVATION,
                FormFeatureType.COMMISSION_FREE
              ]
            },
            [FormEstateType.PLOT]: {
              features: [FormFeatureType.COMMISSION_FREE],
              showPlotFilter: true,
              showProviderTypeFilter: false
            },
            [FormEstateType.PARKING]: {
              showProviderTypeFilter: false,
              features: [FormFeatureType.COMMISSION_FREE]
            },
            [FormEstateType.MISCELLANEOUS]: {
              showRoomsFilter: true,
              showPlotFilter: true,
              showSpaceFilter: true,
              showYearofConstructionFilter: false,
              showProviderTypeFilter: false,
              showEnergyCertificate: true,
              features: [FormFeatureType.COMMISSION_FREE, FormFeatureType.NEED_OF_RENOVATION]
            }
          }
        },
        [UsageType.COMMERCIAL]: {
          [DistributionType.BUY]: {
            [FormEstateType.OFFICE]: {
              showSpaceFilter: true,
              showEnergyCertificate: true,
              features: [
                FormFeatureType.BALCONY_TERRACE,
                FormFeatureType.PARKING_GARAGE,
                FormFeatureType.CELLAR,
                FormFeatureType.FURNISHED,
                FormFeatureType.REDUCE_MOBILITY_ACCESS,
                FormFeatureType.ELEVATOR,
                FormFeatureType.COMMISSION_FREE,
                FormFeatureType.NEED_OF_RENOVATION
              ]
            },
            [FormEstateType.STORAGE_PRODUCTION]: {
              showSpaceFilter: true,
              showEnergyCertificate: true,
              features: [
                FormFeatureType.PARKING_GARAGE,
                FormFeatureType.REDUCE_MOBILITY_ACCESS,
                FormFeatureType.COMMISSION_FREE
              ]
            },
            [FormEstateType.AGRICULTURE_FORESTRY]: {
              showSpaceFilter: true,
              features: [
                FormFeatureType.PARKING_GARAGE,
                FormFeatureType.REDUCE_MOBILITY_ACCESS,
                FormFeatureType.COMMISSION_FREE
              ]
            },
            [FormEstateType.TRADING]: {
              showSpaceFilter: true,
              showEnergyCertificate: true,
              features: [
                FormFeatureType.PARKING_GARAGE,
                FormFeatureType.CELLAR,
                FormFeatureType.REDUCE_MOBILITY_ACCESS,
                FormFeatureType.ELEVATOR,
                FormFeatureType.COMMISSION_FREE
              ]
            },
            [FormEstateType.GASTRONOMY_HOTEL]: {
              showSpaceFilter: true,
              showRoomsFilter: true,
              showYearofConstructionFilter: false,
              showEnergyCertificate: true,
              features: [
                FormFeatureType.BALCONY_TERRACE,
                FormFeatureType.PARKING_GARAGE,
                FormFeatureType.CELLAR,
                FormFeatureType.FURNISHED,
                FormFeatureType.REDUCE_MOBILITY_ACCESS,
                FormFeatureType.ELEVATOR,
                FormFeatureType.COMMISSION_FREE
              ]
            },
            [FormEstateType.PLOT]: {
              showPlotFilter: true,
              features: [FormFeatureType.COMMISSION_FREE]
            },
            [FormEstateType.PARKING]: {
              features: [FormFeatureType.REDUCE_MOBILITY_ACCESS, FormFeatureType.COMMISSION_FREE]
            },
            [FormEstateType.MISCELLANEOUS]: {
              showPlotFilter: true,
              showSpaceFilter: true,
              showEnergyCertificate: true,
              features: [FormFeatureType.COMMISSION_FREE]
            }
          }
        }
      })
    }
  }
};
var EstateSubTypeOptions3 = {
  [FormEstateType.HOUSE_APARTMENT]: [],
  [FormEstateType.HOUSE]: [],
  [FormEstateType.APARTMENT]: [],
  [FormEstateType.PARKING]: [],
  [FormEstateType.PLOT]: [],
  [FormEstateType.AGRICULTURE_FORESTRY]: [],
  [FormEstateType.GASTRONOMY_HOTEL]: [],
  [FormEstateType.OFFICE]: [],
  [FormEstateType.STORAGE_PRODUCTION]: [],
  [FormEstateType.TRADING]: [],
  [FormEstateType.MISCELLANEOUS]: []
};

// src/brands/projects/immowelt/index.ts
var brandConfig7 = {
  brand: "immowelt",
  country: "de",
  defaultLocale: "de",
  variant: "flagship",
  defaultLocation: {
    id: "AD08DE7633",
    // Hamburg
    coordinates: {
      lat: 53.6448575784306,
      lng: 9.629363416912954
    }
  },
  templates: {
    filters: {
      rooms: ExtendedRoomOptions,
      bedrooms: [],
      energyCertificateClasses: EnergyCertificateClasses,
      energyTypes: [],
      estateTypesMinimal: [],
      locationTypes: [
        PlaceType.BOROUGHS,
        PlaceType.NEIGHBORHOOD,
        PlaceType.MUNICIPALITY,
        PlaceType.PROVINCE,
        PlaceType.REGION
      ],
      // see: https://avivgroup.atlassian.net/wiki/spaces/ATSS/pages/396886022/SERP+-+Filters
      ruleSet: ruleSet(defaultFiltersConditions2, EstateSubTypeOptions3, {
        [UsageType.RESIDENTIAL]: {
          [DistributionType.BUY]: {
            [FormEstateType.HOUSE_APARTMENT]: {
              showRoomsFilter: true,
              showSpaceFilter: true,
              showYearofConstructionFilter: false,
              showFloorsFilter: true,
              showProviderTypeFilter: false,
              showEnergyCertificate: true,
              showPlotFilter: true,
              features: [
                FormFeatureType.BALCONY_TERRACE,
                FormFeatureType.GARDEN,
                FormFeatureType.PARKING_GARAGE,
                FormFeatureType.KITCHEN_FULLY_EQUIPPED,
                FormFeatureType.BATHROOM_WINDOW,
                FormFeatureType.BATHROOM_BATHTUB,
                FormFeatureType.CELLAR,
                FormFeatureType.VACANT,
                FormFeatureType.FURNISHED,
                FormFeatureType.UNFURNISHED,
                FormFeatureType.REDUCE_MOBILITY_ACCESS,
                FormFeatureType.ELEVATOR,
                FormFeatureType.COMMISSION_FREE,
                FormFeatureType.NEED_OF_RENOVATION
              ]
            },
            [FormEstateType.HOUSE]: {
              showRoomsFilter: true,
              showSpaceFilter: true,
              showPlotFilter: true,
              showYearofConstructionFilter: false,
              showProviderTypeFilter: false,
              showEnergyCertificate: true,
              features: [
                FormFeatureType.BALCONY_TERRACE,
                FormFeatureType.GARDEN,
                FormFeatureType.PARKING_GARAGE,
                FormFeatureType.KITCHEN_FULLY_EQUIPPED,
                FormFeatureType.BATHROOM_WINDOW,
                FormFeatureType.BATHROOM_BATHTUB,
                FormFeatureType.CELLAR,
                FormFeatureType.VACANT,
                FormFeatureType.FURNISHED,
                FormFeatureType.UNFURNISHED,
                FormFeatureType.REDUCE_MOBILITY_ACCESS,
                FormFeatureType.COMMISSION_FREE,
                FormFeatureType.NEED_OF_RENOVATION
              ]
            },
            [FormEstateType.APARTMENT]: {
              showRoomsFilter: true,
              showSpaceFilter: true,
              showYearofConstructionFilter: false,
              showFloorsFilter: true,
              showProviderTypeFilter: false,
              showEnergyCertificate: true,
              features: [
                FormFeatureType.BALCONY_TERRACE,
                FormFeatureType.GARDEN,
                FormFeatureType.PARKING_GARAGE,
                FormFeatureType.KITCHEN_FULLY_EQUIPPED,
                FormFeatureType.BATHROOM_WINDOW,
                FormFeatureType.BATHROOM_BATHTUB,
                FormFeatureType.CELLAR,
                FormFeatureType.VACANT,
                FormFeatureType.FURNISHED,
                FormFeatureType.UNFURNISHED,
                FormFeatureType.REDUCE_MOBILITY_ACCESS,
                FormFeatureType.ELEVATOR,
                FormFeatureType.NEED_OF_RENOVATION,
                FormFeatureType.COMMISSION_FREE
              ]
            },
            [FormEstateType.PLOT]: {
              features: [FormFeatureType.COMMISSION_FREE],
              showPlotFilter: true,
              showProviderTypeFilter: false
            },
            [FormEstateType.PARKING]: {
              showProviderTypeFilter: false,
              features: [FormFeatureType.COMMISSION_FREE]
            }
          }
        }
      })
    }
  }
};
var brandConfig8 = {
  brand: "selogerneuf",
  country: "fr",
  defaultLocale: "fr",
  variant: "flagship",
  defaultLocation: {
    id: "AD06FR76",
    // Paris
    coordinates: {
      lat: 48.85658483336071,
      lng: 2.3429881280649387
    }
  },
  templates: {
    filters: {
      rooms: StandardRoomOptions,
      bedrooms: StandardBedroomOptions,
      energyCertificateClasses: EnergyCertificateClasses2,
      energyTypes: [],
      estateTypesMinimal: [FormEstateType.HOUSE, FormEstateType.APARTMENT],
      locationTypes: [
        PlaceType.BOROUGHS,
        PlaceType.NEIGHBORHOOD,
        PlaceType.MUNICIPALITY,
        PlaceType.PROVINCE,
        PlaceType.REGION
      ],
      // see: https://avivgroup.atlassian.net/wiki/spaces/ATSS/pages/396886022/SERP+-+Filters
      ruleSet: ruleSet(defaultFiltersConditions2, EstateSubTypeOptions2, {
        [UsageType.RESIDENTIAL]: {
          [DistributionType.BUY]: {
            [FormEstateType.HOUSE_APARTMENT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.HOUSE_APARTMENT],
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.HOUSE_APARTMENT]
            },
            [FormEstateType.HOUSE]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.HOUSE],
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.HOUSE]
            },
            [FormEstateType.APARTMENT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.APARTMENT],
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.APARTMENT]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.PLOT],
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.PLOT]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.PARKING],
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.PARKING]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.MISCELLANEOUS],
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.RESIDENTIAL][DistributionType.BUY][FormEstateType.MISCELLANEOUS]
            }
          }
        },
        [UsageType.COMMERCIAL]: {
          [DistributionType.BUY]: {
            [FormEstateType.OFFICE]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.OFFICE],
              showEnergyCertificate: true,
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.OFFICE]
            },
            [FormEstateType.STORAGE_PRODUCTION]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.STORAGE_PRODUCTION],
              showEnergyCertificate: true,
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.STORAGE_PRODUCTION]
            },
            [FormEstateType.AGRICULTURE_FORESTRY]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.AGRICULTURE_FORESTRY],
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.AGRICULTURE_FORESTRY]
            },
            [FormEstateType.TRADING]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.TRADING],
              showEnergyCertificate: true,
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.TRADING]
            },
            [FormEstateType.GASTRONOMY_HOTEL]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.GASTRONOMY_HOTEL],
              showRoomsFilter: true,
              showYearofConstructionFilter: false,
              showEnergyCertificate: true,
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              features: FeatureOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.GASTRONOMY_HOTEL]
            },
            [FormEstateType.PLOT]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.PLOT],
              showEnergyCertificate: false,
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.PLOT]
            },
            [FormEstateType.PARKING]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.PARKING],
              showEnergyCertificate: false,
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.PARKING]
            },
            [FormEstateType.MISCELLANEOUS]: {
              ...FilterTypeOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.MISCELLANEOUS],
              showPlotFilter: true,
              showSpaceFilter: true,
              showEnergyCertificate: true,
              showProviderTypeFilter: false,
              showListingsDisplay: false,
              showEnergyTypeFilter: false,
              showYearofConstructionFilter: false,
              features: FeatureOptions2[UsageType.COMMERCIAL][DistributionType.BUY][FormEstateType.MISCELLANEOUS]
            }
          }
        }
      })
    }
  }
};

// src/brands/brandConfiguration.ts
var brandsConfiguration = {
  project: {
    immonet: brandConfig6,
    immowelt: brandConfig7,
    seloger: brandConfig6,
    logicimmo: brandConfig6,
    selogerneuf: brandConfig8,
    immoweb: brandConfig6
  },
  classified: {
    immonet: brandConfig,
    immowelt: brandConfig3,
    seloger: brandConfig5,
    logicimmo: brandConfig4,
    selogerneuf: brandConfig8,
    immoweb: brandConfig2
  }
};
var getBrandConfiguration = ({
  brand,
  locale,
  searchType
}) => {
  const brandConfig9 = brandsConfiguration[searchType][brand];
  return {
    brand: brandConfig9?.brand || brand,
    locale: locale || brandConfig9?.defaultLocale,
    country: brandConfig9?.country,
    variant: brandConfig9?.variant,
    defaultLocation: brandConfig9?.defaultLocation,
    templates: brandConfig9?.templates,
    transformers: brandConfig9?.transformers
  };
};
var WithMFEContext = ({
  children,
  brand,
  locale,
  stage,
  countMode,
  searchType
}) => {
  const innerBrand = brand || useBrand();
  const innerLocale = locale || useLocale();
  const innerStage = stage || useStage();
  const innerSearchType = searchType || useSearchType();
  const brandConfig9 = useMemo(
    () => getBrandConfiguration({ brand: innerBrand, locale: innerLocale, searchType: innerSearchType || "classified" }),
    [innerBrand, innerLocale, innerSearchType]
  );
  void i18n_default.changeLanguage(innerLocale);
  return /* @__PURE__ */ jsx(MFEProvider, { ...brandConfig9, stage: innerStage, countMode, searchType, children });
};
var ModalContext = createContext({ submitText: void 0 });
var useModalContext = () => useContext(ModalContext);
var ModalProvider = ({ submitText, children }) => {
  const updatedContext = {
    submitText
  };
  return /* @__PURE__ */ jsx(ModalContext.Provider, { value: updatedContext, children });
};
var SearchContext = createContext({
  count: 0,
  inProgress: false,
  isValid: true,
  isIsochroneLoading: false,
  setCount: () => {
  },
  setInProgress: () => {
  },
  setIsValid: () => {
  },
  setIsIsochroneLoading: () => {
  }
});
var useSearchContext = () => useContext(SearchContext);
var SearchProvider = ({ children }) => {
  const [count, setCount] = useState(0);
  const [inProgress, setInProgress] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [isIsochroneLoading, setIsIsochroneLoading] = useState(false);
  const updatedContext = {
    count,
    inProgress,
    isValid,
    isIsochroneLoading,
    setCount,
    setInProgress,
    setIsValid,
    setIsIsochroneLoading
  };
  useUpdateCount({ setCount, setIsValid, setInProgress });
  return /* @__PURE__ */ jsx(SearchContext.Provider, { value: updatedContext, children });
};

export { ModalProvider, SearchProvider, WithMFEContext, fetchPlaces, getInternalServiceUrl, getServiceUrl, useConditionalFilter, useConditionalFilters, useCountry, useDefaultLocation, useLocale, useModalContext, useRelation, useSearchContext, useSearchType, useServiceUrl, useTemplates, useTransformers, useTranslate, useUpdateLocations, useVariant };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=chunk-SBDRB6T7.js.map